import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiErrorDetail } from "@/lib/api";
import { getCaseProgress } from "@/lib/caseProgress";
import { usePresenceWebSocket } from "@/hooks/usePresenceWebSocket";
import { PresenceBar } from "@/components/app/PresenceBar";
import { ReadReceipt } from "@/components/app/ReadReceipt";
import { MessageExportDialog } from "@/components/app/MessageExportDialog";
import Header from "@/components/app/Header";
import ConveyancingTimeline from "@/components/app/ConveyancingTimeline";
import ClientDetailsDialog from "@/components/app/ClientDetailsDialog";
import GenerateFromTemplateButton from "@/components/app/GenerateFromTemplateButton";
import PartiesCard from "@/components/app/PartiesCard";
import PropertyDetailsCard from "@/components/app/PropertyDetailsCard";
import ManualTitleRiskCard from "@/components/app/ManualTitleRiskCard";
import HmlrRequestsCard from "@/components/app/HmlrRequestsCard";
import ConveyancingFeesCard from "@/components/app/ConveyancingFeesCard";
import CommunicationsTab from "@/components/app/CommunicationsTab";
import CaseAccountsTab from "@/components/app/CaseAccountsTab";
import AuthoritiesTab from "@/components/app/AuthoritiesTab";
import OnboardingStatusPanel from "@/components/app/OnboardingStatusPanel";
import ESignPanel from "@/components/app/esign/ESignPanel";
import SendForSigningDialog from "@/components/app/esign/SendForSigningDialog";
import FieldDesigner from "@/components/app/esign/FieldDesigner";
import ReviewInheritanceDialog from "@/components/app/ReviewInheritanceDialog";
import AgentNotesPanel from "@/components/app/AgentNotesPanel";
import QuestionnaireCard from "@/components/client-portal/QuestionnaireCard";
import PostCompletionSummaryCard from "@/components/app/PostCompletionSummaryCard";
import DocumentViewerDialog from "@/components/app/DocumentViewerDialog";
import { SignatureChip } from "@/components/app/SignatureChip";
import { MessageAttachmentView, MessageGalleryProvider } from "@/components/app/MessageAttachmentView";
import { PaperPlaneRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import BulkActionBar from "@/components/app/BulkActionBar";
import FileNotesPanel from "@/components/app/FileNotesPanel";
import ReportDetailsPanel from "@/components/app/ReportDetailsPanel";
import CaseExportMenu from "@/components/app/CaseExportMenu";
import AiDraftDialog from "@/components/app/AiDraftDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Clock, FileText, ListChecks, ChatCircle,
  Plus, UploadSimple, DownloadSimple, Check, CalendarBlank, Paperclip, Files, FolderSimple,
  PaperPlaneTilt, User, WifiHigh, WifiSlash, Warning, UserCircle,
  LinkSimple, Copy, Trash, FilePdf, CloudArrowUp, Tag,
  ArrowsClockwise, Phone, EnvelopeSimple, MapTrifold, Briefcase,
  FloppyDisk, StackSimple, Archive, Receipt, Prohibit, AddressBook, Sparkle,
  Hash, PencilSimple, X, Folder, FolderOpen, EyeSlash, DotsThreeVertical,
  MagnifyingGlass, CaretDown, Scales, Eye, Info, CheckCircle, CircleDashed,
  PushPin, PushPinSlash, Stamp, ShieldCheck, ClipboardText, Signature,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";

/**
 * Compact search input slotted into a chat panel header. Filters the visible
 * message list locally (no API call). Replaces the previous global ⌘K
 * search dialog — search now lives where the conversation is.
 *
 * Props:
 *   value, onChange — controlled input
 *   testid          — base data-testid (we generate `-input` and `-clear`)
 *   tone            — "slate" (default) or "violet" to match panel accent
 */
function ChatSearchInput({ value, onChange, testid, tone = "slate" }) {
  const ringClass = tone === "violet"
    ? "focus-within:ring-violet-200 focus-within:border-violet-300"
    : "focus-within:ring-blue-200 focus-within:border-blue-300";
  return (
    <div className={`inline-flex items-center gap-1.5 h-7 px-2 rounded-full bg-white border border-slate-200 transition-all focus-within:ring-2 ${ringClass}`}>
      <MagnifyingGlass weight="bold" className="w-3 h-3 text-slate-400" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search…"
        data-testid={`${testid}-input`}
        className="text-[11px] outline-none bg-transparent w-28 sm:w-36 placeholder:text-slate-400"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid={`${testid}-clear`}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Clear search"
        >
          <X weight="bold" className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

/**
 * Inline-editable case reference pill. Solicitors click to edit, hit
 * Enter / blur to save. Clients see a read-only "Ref · UKC-2026-0042" pill
 * (no pill at all if the case has no ref). On save we PUT /cases/{id} and
 * notify the parent so the header reflects the new value immediately.
 */
function CaseRefPill({ caseId, value, isSolicitor, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  // Keep draft in sync when the parent re-fetches the case.
  useEffect(() => { setDraft(value || ""); }, [value]);

  const commit = async () => {
    const next = draft.trim();
    if ((next || null) === (value || null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await API.put(`/cases/${caseId}`, { case_ref: next });
      onSaved?.(data.case_ref || null);
      toast.success(next ? "Case reference saved" : "Case reference cleared");
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save case ref");
    } finally {
      setSaving(false);
    }
  };

  if (!isSolicitor) {
    if (!value) return null;
    return (
      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700"
        data-testid="case-ref-pill-readonly">
        <Hash weight="bold" className="w-3 h-3" /> {value}
      </span>
    );
  }

  if (editing) {
    return (
      <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-white border border-slate-300"
        data-testid="case-ref-pill-edit">
        <Hash weight="bold" className="w-3 h-3 text-slate-400" />
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
          }}
          disabled={saving}
          maxLength={48}
          placeholder="UKC-2026-0042"
          data-testid="case-ref-input"
          className="h-5 outline-none bg-transparent text-xs w-32 disabled:opacity-50"
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid="case-ref-pill"
      title={value ? "Edit case reference" : "Add a case reference"}
      className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full transition-colors ${
        value
          ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
          : "bg-white border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700"
      }`}
    >
      <Hash weight="bold" className="w-3 h-3" /> {value || "Add ref"}
      <PencilSimple weight="regular" className="w-2.5 h-2.5 opacity-60" />
    </button>
  );
}

/**
 * Per-case email reference pill. Shows the case's auto-generated `[Ref: XXXXXX]`
 * tag with one-click copy. Solicitors paste it into the subject line or their
 * signature when emailing this matter from Outlook (outside the app) so replies
 * thread/auto-file back to the case — the inbox matcher reads the tag from both
 * subject and body. Solicitors only.
 */
function EmailRefPill({ autoRef }) {
  const [copied, setCopied] = useState(false);
  if (!autoRef) return null;
  const tag = `[Ref: ${autoRef}]`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tag);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Email ref copied — paste it into your Outlook subject or signature for this matter");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid="case-email-ref-copy"
      title="Copy this matter's email reference. Paste it into the subject line or your signature when emailing from Outlook so replies auto-file to this case."
      className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
    >
      {copied ? <Check weight="bold" className="w-3 h-3" /> : <Copy weight="bold" className="w-3 h-3" />}
      {tag}
    </button>
  );
}


/**
 * Inline editor for `case.referral_fee`. Solicitor-only. Shows the fee as a
 * small violet badge with a pencil icon — click to edit, Enter / blur to save,
 * Esc to cancel. Empty value clears the fee. Backed by
 * PUT /cases/{id}/referral-fee — also handles retroactive invoice creation
 * when the case is already completed.
 *
 * Props:
 *   caseId        — case to update
 *   value         — current fee (number | null | undefined)
 *   disabled      — true if no referrer is assigned (we then hide the editor)
 *   onSaved(case) — receives the updated case from the backend
 */
function ReferralFeeEditor({ caseId, value, disabled, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value != null ? String(value) : "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(value != null ? String(value) : "");
  }, [value]);

  if (disabled) return null;

  const fmt = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency", currency: "GBP", minimumFractionDigits: 0,
    }).format(Number(n) || 0);

  const commit = async () => {
    if (saving) return;
    const trimmed = (draft || "").trim();
    let payload;
    if (!trimmed) {
      payload = null;
    } else {
      const num = Number(trimmed);
      if (Number.isNaN(num) || num < 0) {
        toast.error("Enter a valid amount");
        return;
      }
      payload = num;
    }
    // No-op — same as current value
    const current = value == null ? null : Number(value);
    if (payload === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await API.put(`/cases/${caseId}/referral-fee`, { amount: payload });
      onSaved?.(data);
      toast.success(payload == null ? "Referral fee cleared" : "Referral fee saved");
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save fee");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-violet-300"
        data-testid="referral-fee-edit">
        <span className="text-[10px] text-violet-600 font-semibold">£</span>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { setDraft(value != null ? String(value) : ""); setEditing(false); }
          }}
          disabled={saving}
          inputMode="decimal"
          placeholder="0"
          data-testid="referral-fee-input"
          className="h-4 outline-none bg-transparent text-[11px] w-16 disabled:opacity-50"
        />
      </span>
    );
  }

  const hasFee = typeof value === "number" && value > 0;
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid="referral-fee-edit-btn"
      title={hasFee ? "Edit referral fee" : "Set referral fee"}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${
        hasFee
          ? "bg-violet-100 text-violet-700 hover:bg-violet-200"
          : "bg-white border border-dashed border-violet-300 text-violet-600 hover:border-violet-500"
      }`}
    >
      <span data-testid="referral-fee-amount">
        {hasFee ? `Fee: ${fmt(value)}` : "Set fee"}
      </span>
      <PencilSimple weight="regular" className="w-2.5 h-2.5 opacity-70" />
    </button>
  );
}

/**
 * Compact dialog for solicitors to edit a case's title and property address
 * after the case has been opened (typo fixes, address corrections, etc.).
 * PUT /api/cases/{id} accepts both fields; we send only the ones that
 * actually changed.
 */
function EditCaseHeaderDialog({ open, onOpenChange, caseData, onSaved }) {
  const [title, setTitle] = useState(caseData?.title || "");
  const [address, setAddress] = useState(caseData?.property_address || "");
  const [exchangeDate, setExchangeDate] = useState(caseData?.target_exchange_date || "");
  const [completionDate, setCompletionDate] = useState(caseData?.target_completion_date || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(caseData?.title || "");
      setAddress(caseData?.property_address || "");
      setExchangeDate(caseData?.target_exchange_date || "");
      setCompletionDate(caseData?.target_completion_date || "");
    }
  }, [open, caseData]);

  const handleSave = async () => {
    const t = title.trim();
    const a = address.trim();
    if (!t) { toast.error("Case name is required"); return; }
    if (!a) { toast.error("Property address is required"); return; }
    const payload = {};
    if (t !== (caseData?.title || "")) payload.title = t;
    if (a !== (caseData?.property_address || "")) payload.property_address = a;
    if (exchangeDate !== (caseData?.target_exchange_date || "")) payload.target_exchange_date = exchangeDate || "";
    if (completionDate !== (caseData?.target_completion_date || "")) payload.target_completion_date = completionDate || "";
    if (Object.keys(payload).length === 0) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await API.put(`/cases/${caseData.id}`, payload);
      onSaved?.(data);
      toast.success("Case updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update case");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-case-header-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Case</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Case name</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., 12 Acacia Avenue — Smith Purchase"
              maxLength={120}
              data-testid="edit-case-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Property address</Label>
            <Textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Full property address"
              rows={2}
              data-testid="edit-case-address-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Target exchange</Label>
              <Input
                type="date"
                value={exchangeDate}
                onChange={(e) => setExchangeDate(e.target.value)}
                data-testid="edit-case-exchange-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Target completion</Label>
              <Input
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                data-testid="edit-case-completion-date"
              />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Setting target dates will mirror them to the assigned solicitor's Outlook calendar (if they've connected their account on Settings → Integrations).
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="edit-case-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="edit-case-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "timeline";
  const [activeTab, setActiveTab] = useState(initialTab);
  const focusPanel = searchParams.get("focus"); // "referrer" | "client" | null
  // Message-level deep-link — set when the solicitor clicks an Inbox
  // → Messages row. The Notes tab scrolls to and briefly ring-highlights
  // the matching entry so they land on the message they were pinged
  // about, not a generic notes list.
  const highlightMessageId = searchParams.get("highlight") || null;
  const highlightChannel = searchParams.get("channel") || null;  // "referrer" | "client"
  const isSolicitor = user?.role === "solicitor";

  const [caseData, setCaseData] = useState(null);
  const [caseInvoice, setCaseInvoice] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportSelection, setExportSelection] = useState(new Set()); // keys: "case_note:<id>" or "referrer_note:<id>"
  const [documents, setDocuments] = useState([]);
  // "Send for signature" dialog state — case Documents tab. Only PDFs
  // can be sent (DocuSign expects PDF input). Opens a dialog to pick
  // signer name/email + email subject; posts to /api/docusign/envelopes
  // which downloads the doc server-side, wraps it in an envelope and
  // fires it off. Solicitor identity comes from the auth token — never
  // trusted from client input.
  const [sigDialog, setSigDialog] = useState({ open: false, doc: null, mode: "standard", signer_name: "", signer_email: "", signer_phone: "", email_subject: "", email_blurb: "Please review and sign at your convenience.", include_witness: false, witness_name: "", witness_email: "", sending: false });
  // Per-case folder state for the Documents tab. Folders are 8 presets (auto-
  // seeded on case creation) + any custom ones the solicitor has added.
  // ``activeFolderId`` is the currently-selected pane: null = case root
  // (unfiled docs), "all" = show every doc regardless of folder, otherwise
  // a specific folder uuid. Custom folder dialog state keeps things simple.
  const [folders, setFolders] = useState([]);
  // Native e-signing (launched from a PDF in the Documents folder).
  const [esignModeDoc, setEsignModeDoc] = useState(null);
  const [esignDesigner, setEsignDesigner] = useState(null);
  const [esignRefresh, setEsignRefresh] = useState(0);
  const [esignEnvelopes, setEsignEnvelopes] = useState([]);

  // Pull signing envelopes for the case so we can badge documents with their
  // signing status (out for signing / signed / PG82 / certificate).
  useEffect(() => {
    if (!isSolicitor) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get(`/signing/envelopes`, { params: { case_id: caseId } });
        if (!cancelled) setEsignEnvelopes(data.envelopes || []);
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [caseId, isSolicitor, esignRefresh]);

  const docSigningStatus = useMemo(() => {
    const rank = { draft: 1, in_progress: 2, awaiting_solicitor: 3, completed: 4 };
    const srcLabel = { draft: "Draft signing", in_progress: "Out for signing", awaiting_solicitor: "Awaiting completion", completed: "Signed" };
    const srcCls = { draft: "bg-slate-100 text-slate-600", in_progress: "bg-amber-100 text-amber-800", awaiting_solicitor: "bg-blue-100 text-blue-800", completed: "bg-emerald-100 text-emerald-800" };
    const m = {};
    for (const env of esignEnvelopes) {
      if (env.status === "voided") continue;
      const srcIds = env.source_document_ids && env.source_document_ids.length ? env.source_document_ids : (env.source_document_id ? [env.source_document_id] : []);
      if (srcLabel[env.status]) {
        for (const sid of srcIds) {
          const prev = m[sid];
          if (!prev || (rank[env.status] || 0) > (prev.rank || 0)) {
            m[sid] = { label: srcLabel[env.status], cls: srcCls[env.status], rank: rank[env.status] };
          }
        }
      }
      if (env.signed_document_id) m[env.signed_document_id] = { label: "Signed copy", cls: "bg-emerald-100 text-emerald-800", rank: 99 };
      if (env.pg82_document_id) m[env.pg82_document_id] = { label: "PG82", cls: "bg-indigo-100 text-indigo-800", rank: 99 };
      if (env.audit_document_id) m[env.audit_document_id] = { label: "Certificate", cls: "bg-slate-100 text-slate-600", rank: 99 };
    }
    return m;
  }, [esignEnvelopes]);
  const [activeFolderId, setActiveFolderId] = useState("all");
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderHidden, setNewFolderHidden] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const [reopenGuard, setReopenGuard] = useState(null); // {stageKey, subId, stageLabel} or null
  // Per-chat-window search — filters the visible message list locally without
  // a server round-trip (notes are already loaded). Replaces the previous
  // global header search since users want search where the conversation is.
  const [clientChatSearch, setClientChatSearch] = useState("");
  const [referrerChatSearch, setReferrerChatSearch] = useState("");
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [referrerNotes, setReferrerNotes] = useState([]);
  const clientMessagesEndRef = useRef(null);
  const referrerMessagesEndRef = useRef(null);
  const [referrerNoteText, setReferrerNoteText] = useState("");
  const [sendingReferrerNote, setSendingReferrerNote] = useState(false);
  const [noteFiles, setNoteFiles] = useState([]); // pending client-message attachments
  const [referrerNoteFiles, setReferrerNoteFiles] = useState([]); // pending referrer-message attachments
  const [uploadingNoteFile, setUploadingNoteFile] = useState(false);
  const [uploadingReferrerFile, setUploadingReferrerFile] = useState(false);
  const noteFileInputRef = useRef(null);
  const referrerFileInputRef = useRef(null);
  const [dragChannel, setDragChannel] = useState(null); // "client" | "referrer" | null
  const dragDepthRef = useRef(0);
  const [docPickerChannel, setDocPickerChannel] = useState(null); // open doc picker for channel
  const [docPickerSearch, setDocPickerSearch] = useState("");
  const [convertingDocId, setConvertingDocId] = useState(null);
  const [bulkFolderAction, setBulkFolderAction] = useState(null); // "move" | "copy" | null
  const [loading, setLoading] = useState(true);

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [newTask, setNewTask] = useState({ title: "", description: "", due_date: "", assigned_to: "" });
  const [uploading, setUploading] = useState(false);
  const [sendingNote, setSendingNote] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [calendarDate, setCalendarDate] = useState(undefined);
  const [caseMembers, setCaseMembers] = useState([]);
  const [shareToken, setShareToken] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [addClientEmail, setAddClientEmail] = useState("");
  const [addClientName, setAddClientName] = useState("");
  const [addClientPhone, setAddClientPhone] = useState("");
  const [addClientRole, setAddClientRole] = useState("buyer");
  // Client details dialog — holds the currently-open client object, or null
  // when the dialog is closed. Mounts on demand so the >12-field form
  // doesn't render for every client all the time.
  const [editingClient, setEditingClient] = useState(null);
  const [addingClient, setAddingClient] = useState(false);
  const [editCaseHeaderOpen, setEditCaseHeaderOpen] = useState(false);
  // Inline-expand state for each client row on the sidebar — shows extended
  // profile (DOB / address / AML / ID / etc.) inline without opening the
  // edit dialog. Single-row expansion keeps the rail compact.
  const [expandedClientKey, setExpandedClientKey] = useState(null);
  // Client PII inheritance review — {key, entry} triggers ReviewInheritanceDialog
  const [inheritanceReview, setInheritanceReview] = useState(null);
  const [solicitors, setSolicitors] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedSolicitorId, setSelectedSolicitorId] = useState("");
  // Linked client lead (admin only — case was opened from a contact-only lead)
  const [leadInfo, setLeadInfo] = useState(null);
  const [promoteLeadOpen, setPromoteLeadOpen] = useState(false);
  const [promoteLeadPwd, setPromoteLeadPwd] = useState("");
  const [promotingLead, setPromotingLead] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelCategory, setCancelCategory] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancellingCase, setCancellingCase] = useState(false);
  const [deletingCase, setDeletingCase] = useState(false);
  const [archivingCase, setArchivingCase] = useState(false);
  const [aiDraftOpen, setAiDraftOpen] = useState(false);
  const [referrers, setReferrers] = useState([]);
  const [assigningReferrer, setAssigningReferrer] = useState(false);

  const CLIENT_ROLES = [
    { value: "buyer", label: "Buyer" },
    { value: "seller", label: "Seller" },
    { value: "guarantor", label: "Guarantor" },
    { value: "joint_buyer", label: "Joint Buyer" },
    { value: "executor", label: "Executor" },
    { value: "power_of_attorney", label: "Power of Attorney" },
    { value: "other", label: "Other" },
  ];

  const ROLE_COLORS = {
    buyer: "bg-blue-100 text-blue-700",
    seller: "bg-amber-100 text-amber-700",
    guarantor: "bg-violet-100 text-violet-700",
    joint_buyer: "bg-cyan-100 text-cyan-700",
    executor: "bg-rose-100 text-rose-700",
    power_of_attorney: "bg-emerald-100 text-emerald-700",
    other: "bg-slate-100 text-slate-600",
  };
  const [isDragging, setIsDragging] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("uncategorised");
  const [filterCategory, setFilterCategory] = useState("all");
  const [checklist, setChecklist] = useState([]);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  // Document viewer auto-jump + highlight (set when launched from a PDF
  // search hit). Cleared when the dialog closes.
  const [viewerInitialPage, setViewerInitialPage] = useState(null);
  const [viewerHighlight, setViewerHighlight] = useState("");

  // ---------- Per-case PDF text search (Documents tab) ----------
  // Lazy-indexed by the backend on first query; subsequent searches are
  // sub-second. Results group by document with one entry per matching
  // page; clicking a hit opens the viewer on that page with the match
  // highlighted via react-pdf's customTextRenderer.
  const [pdfSearchQuery, setPdfSearchQuery] = useState("");
  const [pdfSearchResults, setPdfSearchResults] = useState(null); // null = not searched yet
  const [pdfSearchLoading, setPdfSearchLoading] = useState(false);
  const [pdfSearchError, setPdfSearchError] = useState(null);

  const runPdfSearch = async (e) => {
    e?.preventDefault?.();
    const q = pdfSearchQuery.trim();
    if (!q) {
      setPdfSearchResults(null);
      setPdfSearchError(null);
      return;
    }
    setPdfSearchLoading(true);
    setPdfSearchError(null);
    try {
      const { data } = await API.get(`/cases/${caseId}/documents/search`, { params: { q } });
      setPdfSearchResults(data || []);
    } catch (err) {
      setPdfSearchError(err.response?.data?.detail || "Search failed");
      setPdfSearchResults([]);
    } finally {
      setPdfSearchLoading(false);
    }
  };

  const openSearchHit = (hit) => {
    // Resolve the full document object from local state so the viewer
    // gets the same shape as a normal click (mime_type, folder, etc.).
    const fullDoc = documents.find((d) => d.id === hit.doc_id) || {
      id: hit.doc_id, original_filename: hit.filename, mime_type: hit.mime_type,
    };
    setViewerInitialPage(hit.page);
    setViewerHighlight(pdfSearchQuery.trim());
    setViewerDoc(fullDoc);
  };

  const closeViewer = () => {
    setViewerDoc(null);
    setViewerInitialPage(null);
    setViewerHighlight("");
  };

  const DOC_CATEGORIES = [
    { value: "uncategorised", label: "Uncategorised" },
    { value: "id_verification", label: "ID Verification" },
    { value: "proof_of_funds", label: "Proof of Funds" },
    { value: "contracts", label: "Contracts" },
    { value: "searches", label: "Searches" },
    { value: "mortgage", label: "Mortgage" },
    { value: "correspondence", label: "Correspondence" },
    { value: "other", label: "Other" },
  ];

  const CATEGORY_COLORS = {
    uncategorised: "bg-slate-100 text-slate-600",
    id_verification: "bg-violet-100 text-violet-700",
    proof_of_funds: "bg-emerald-100 text-emerald-700",
    contracts: "bg-blue-100 text-blue-700",
    searches: "bg-amber-100 text-amber-700",
    mortgage: "bg-cyan-100 text-cyan-700",
    correspondence: "bg-rose-100 text-rose-700",
    other: "bg-slate-100 text-slate-600",
  };

  const fetchAll = useCallback(async () => {
    try {
      const [caseRes, docsRes, tasksRes, notesRes, checklistRes, foldersRes] = await Promise.all([
        API.get(`/cases/${caseId}`),
        API.get(`/cases/${caseId}/documents`),
        API.get(`/cases/${caseId}/tasks`),
        API.get(`/cases/${caseId}/notes`),
        API.get(`/cases/${caseId}/checklist`).catch(() => ({ data: [] })),
        API.get(`/cases/${caseId}/folders`).catch(() => ({ data: [] })),
      ]);
      setCaseData(caseRes.data);
      setDocuments(docsRes.data);
      setTasks(tasksRes.data);
      setNotes(notesRes.data);
      setChecklist(checklistRes.data);
      setFolders(foldersRes.data || []);
      // If the case was opened from a client lead (no signed-up client), pull
      // the lead's contact details so admins can see/promote inline.
      if (caseRes.data?.lead_id && user?.is_admin) {
        try {
          const { data: lead } = await API.get(`/admin/client-leads/${caseRes.data.lead_id}`);
          setLeadInfo(lead);
        } catch {
          setLeadInfo(null);
        }
      } else {
        setLeadInfo(null);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load case details");
    } finally {
      setLoading(false);
    }
  }, [caseId, user?.is_admin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch case members when task dialog opens
  useEffect(() => {
    if (taskDialogOpen && caseId) {
      API.get(`/cases/${caseId}/members`).then(({ data }) => setCaseMembers(data)).catch(() => {});
    }
  }, [taskDialogOpen, caseId]);

  // Fetch existing share link
  useEffect(() => {
    if (isSolicitor && caseId) {
      API.get(`/cases/${caseId}/share`).then(({ data }) => { if (data.token) setShareToken(data.token); }).catch(() => {});
      API.get("/referrers").then(({ data }) => setReferrers(data)).catch(() => {});
    }
  }, [isSolicitor, caseId]);

  // Fetch referrer notes (solicitor only, when a referrer is assigned)
  useEffect(() => {
    if (isSolicitor && caseId && caseData?.referrer_id) {
      API.get(`/referrer-notes/${caseId}`)
        .then(({ data }) => setReferrerNotes(data))
        .catch(() => setReferrerNotes([]));
    } else {
      setReferrerNotes([]);
    }
  }, [isSolicitor, caseId, caseData?.referrer_id]);

  // Auto-scroll chat panels to the newest message whenever the list changes
  // (initial load + every new message). `behavior: "auto"` keeps it instant on
  // first render, smooth on subsequent appends.
  const scrollToBottom = (ref) => {
    if (ref.current) {
      try { ref.current.scrollIntoView({ behavior: "auto", block: "end" }); } catch {}
    }
  };
  useEffect(() => { scrollToBottom(clientMessagesEndRef); }, [notes.length]);
  useEffect(() => { scrollToBottom(referrerMessagesEndRef); }, [referrerNotes.length]);

  // Deep-link from Inbox → Messages → this specific note. Scroll the
  // matching bubble into view a short beat after the tab renders so
  // it's on-screen with a ring-highlight the solicitor can spot.
  useEffect(() => {
    if (!highlightMessageId || activeTab !== "notes") return;
    if (notes.length === 0 && referrerNotes.length === 0) return;
    const timer = setTimeout(() => {
      // Try both channels so a mislabelled `channel` param still lands.
      const sources = highlightChannel === "referrer"
        ? ["referrer_note"]
        : highlightChannel === "client"
          ? ["case_note"]
          : ["referrer_note", "case_note"];
      for (const src of sources) {
        const el = document.getElementById(`msg-${src}-${highlightMessageId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          break;
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [highlightMessageId, highlightChannel, activeTab, notes.length, referrerNotes.length]);

  // Fetch invoice for this case (auto-generated when completed)
  useEffect(() => {
    if (isSolicitor && caseData?.status === "completed" && caseData?.referrer_id) {
      API.get("/invoices")
        .then(({ data }) => {
          const inv = data.find((i) => i.case_id === caseId);
          setCaseInvoice(inv || null);
        })
        .catch(() => setCaseInvoice(null));
    } else {
      setCaseInvoice(null);
    }
  }, [isSolicitor, caseId, caseData?.status, caseData?.referrer_id, caseData?.referrer_fee_paid]);

  const handleAssignReferrer = async (referrerId) => {
    setAssigningReferrer(true);
    try {
      const { data } = await API.put(`/cases/${caseId}/referrer`, { referrer_id: referrerId || null });
      setCaseData(data);
      toast.success(referrerId ? "Referrer assigned" : "Referrer removed");
    } catch (err) {
      toast.error("Failed to assign referrer");
    } finally {
      setAssigningReferrer(false);
    }
  };

  const handleGenerateShareLink = async () => {
    setGeneratingLink(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/share`);
      setShareToken(data.token);
      toast.success("Share link generated");
    } catch (err) {
      toast.error("Failed to generate link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyShareLink = () => {
    const url = `${window.location.origin}/portal/${shareToken}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const handleRevokeShareLink = async () => {
    try {
      await API.delete(`/cases/${caseId}/share`);
      setShareToken(null);
      toast.success("Share link revoked");
    } catch {
      toast.error("Failed to revoke link");
    }
  };

  const handlePromoteLead = async () => {
    if (!leadInfo) return;
    if (!promoteLeadPwd || promoteLeadPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setPromotingLead(true);
    try {
      const { data } = await API.post(`/admin/client-leads/${leadInfo.id}/promote`, { password: promoteLeadPwd });
      toast.success(`Lead promoted — ${data.user.email} can now log in`);
      setPromoteLeadOpen(false);
      setPromoteLeadPwd("");
      // After promotion, attach the new client account to this case
      try {
        await API.post(`/cases/${caseId}/clients`, { email: data.user.email, case_role: "buyer" });
      } catch { /* attach is best-effort */ }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Promote failed");
    } finally {
      setPromotingLead(false);
    }
  };

  const handleResendInvite = async (client) => {
    try {
      const { data } = await API.post(`/cases/${caseId}/clients/${encodeURIComponent(client.email)}/resend-invite`);
      if (data.sent) {
        // Distinguish real send from mock so the user knows whether an
        // email actually went out via Microsoft Graph. Outlook = real
        // delivery; mock = no provider configured (preview only).
        if (data.mode === "outlook") {
          toast.success(
            `Sent via Outlook ✓ to ${client.email}`,
            { description: data.message_id ? `Microsoft message-id: ${data.message_id.slice(0, 40)}…` : undefined, duration: 6000 },
          );
        } else if (data.mode === "mock") {
          toast.warning(
            `Mocked — no email provider configured`,
            { description: `No real email went out. (${client.email})`, duration: 8000 },
          );
        } else if (data.mode === "blocked") {
          toast.warning(
            `Held by preview safety filter`,
            {
              description: `${client.email} is not on the OUTBOUND_EMAIL_ALLOWLIST. The email was NOT delivered — in production it would send normally. Ask an admin to allowlist this address if you need to test delivery on preview.`,
              duration: 12000,
            },
          );
        } else {
          toast.success(`Invite sent to ${client.email} (${data.mode || "unknown"})`);
        }
      } else {
        toast.error(
          `Couldn't send invite to ${client.email}`,
          { description: data.error || "Check email settings or token expiry", duration: 8000 },
        );
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to resend invite");
    }
  };

  const handleAddClient = async () => {
    if (!addClientEmail.trim()) return;
    setAddingClient(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/clients`, {
        email: addClientEmail.trim(),
        case_role: addClientRole,
        name: addClientName.trim() || undefined,
        phone: addClientPhone.trim() || undefined,
      });
      setCaseData(data);
      setAddClientEmail("");
      setAddClientName("");
      setAddClientPhone("");
      setAddClientRole("buyer");
      // The backend stamps `pending_account: true` when no user existed —
      // surface that in the toast so the solicitor knows the next step.
      const justAdded = (data.clients || []).find((c) =>
        (c.email || "").toLowerCase() === addClientEmail.trim().toLowerCase()
      );
      if (justAdded?.pending_account) {
        toast.success("Client saved — they'll auto-link to this case when they create their account");
      } else {
        toast.success("Client added to case");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAddingClient(false);
    }
  };

  const handleRemoveClient = async (client) => {
    // Pending clients have no `id` — pass `pending:<email>` instead so the
    // backend can locate them.
    const removeId = client.id || `pending:${client.email}`;
    try {
      const { data } = await API.delete(`/cases/${caseId}/clients/${removeId}`);
      setCaseData(data);
      toast.success("Client removed from case");
    } catch (err) {
      toast.error("Failed to remove client");
    }
  };

  const handleUpdateClientRole = async (client, newRole) => {
    if (!client.id) {
      // Update locally for pending clients via the same API used for accounts.
      // (We let the user reorder roles before signup.)
      try {
        await API.put(`/cases/${caseId}/clients/pending:${client.email}/role`, { case_role: newRole });
      } catch {
        // Backend lacks a /role endpoint for pending — silently update local state instead
      }
      setCaseData((prev) => prev ? ({
        ...prev,
        clients: (prev.clients || []).map((c) =>
          c.email === client.email && !c.id ? { ...c, case_role: newRole } : c
        ),
      }) : prev);
      return;
    }
    try {
      const { data } = await API.put(`/cases/${caseId}/clients/${client.id}/role`, { case_role: newRole });
      setCaseData(data);
    } catch (err) {
      toast.error("Failed to update role");
    }
  };

  /**
   * Order a Thirdfort AML/ID-V check for one client on the case.
   * Works for both saved clients (by .id) and pending clients (via
   * `pending:{email}` lookup, since AML is often ordered BEFORE the
   * client accepts the portal invite). The verification link is
   * emailed to the client; status mirrors back onto
   * `case.clients[].aml_*` so the row picks up the verdict chip
   * without an extra fetch.
   */
  const handleOrderAmlCheck = async (client) => {
    if (!client.email) {
      toast.error("Client has no email address.");
      return;
    }
    const lookupKey = client.id || `pending:${client.email}`;
    const tid = toast.loading(`Ordering AML check for ${client.name || client.email}…`);
    try {
      await API.post(`/cases/${caseId}/clients/${lookupKey}/aml-check`, {
        send_email: true,
      });
      // Reload the case to pick up the mirrored aml_* fields
      const { data } = await API.get(`/cases/${caseId}`);
      setCaseData(data);
      toast.success(`AML check ordered — verification link emailed to ${client.email}`, { id: tid });
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to order AML check";
      toast.error(msg, { id: tid });
    }
  };

  const handleReassignCase = async () => {
    if (!selectedSolicitorId) return;
    setReassigning(true);
    try {
      const { data } = await API.put(`/cases/${caseId}/reassign`, { solicitor_id: selectedSolicitorId });
      setCaseData(data);
      setReassignDialogOpen(false);
      setSelectedSolicitorId("");
      toast.success("Case reassigned");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReassigning(false);
    }
  };

  const handleArchiveCase = async () => {
    setArchivingCase(true);
    try {
      if (caseData.status === "archived") {
        const { data } = await API.put(`/cases/${caseId}/unarchive`);
        setCaseData(data);
        toast.success("Case restored");
      } else {
        const { data } = await API.put(`/cases/${caseId}/archive`);
        setCaseData(data);
        toast.success("Case archived");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setArchivingCase(false);
    }
  };

  const handleCancelCase = async () => {
    setCancellingCase(true);
    try {
      const { data } = await API.put(`/cases/${caseId}/cancel`, {
        category: cancelCategory || undefined,
        reason: cancelReason.trim() || undefined,
      });
      setCaseData(data);
      toast.success(caseData?.referrer_id
        ? "Case cancelled · referrer notified, fee removed"
        : "Case cancelled — no fee will apply");
      setCancelDialogOpen(false);
      setCancelCategory("");
      setCancelReason("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCancellingCase(false);
    }
  };

  const handleDeleteCase = async () => {
    setDeletingCase(true);
    try {
      await API.delete(`/cases/${caseId}`);
      toast.success("Case moved to Recently Deleted (30-day grace period)");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeletingCase(false);
    }
  };

  // Fetch solicitors when reassign dialog opens
  useEffect(() => {
    if (reassignDialogOpen) {
      API.get("/users/solicitors").then(({ data }) => setSolicitors(data)).catch(() => {});
    }
  }, [reassignDialogOpen]);

  // WebSocket for real-time updates
  const handleWsMessage = useCallback((msg) => {
    switch (msg.type) {
      case "stage_updated":
      case "stages_updated":
        setCaseData(msg.data);
        toast.info("Timeline updated");
        break;
      case "agent_notes_updated":
        setCaseData(msg.data);
        break;
      case "document_uploaded":
        setDocuments((prev) => [msg.data, ...prev]);
        toast.info("New document uploaded");
        break;
      case "documents_bulk_uploaded":
        // Refresh full doc list on bulk upload
        API.get(`/cases/${caseId}/documents`).then(({ data }) => setDocuments(data)).catch(() => {});
        toast.info(`${msg.data.count} documents uploaded`);
        break;
      case "task_created":
        setTasks((prev) => [...prev, msg.data]);
        toast.info("New task added");
        break;
      case "task_updated":
        setTasks((prev) => prev.map((t) => t.id === msg.data.id ? msg.data : t));
        break;
      case "note_created":
        setNotes((prev) => {
          if (prev.some((n) => n.id === msg.data.id)) return prev;
          return [msg.data, ...prev];
        });
        break;
      case "notes_marked_seen":
        // Refresh notes to update seen_by arrays
        API.get(`/cases/${caseId}/notes`).then(({ data }) => setNotes(data)).catch(() => {});
        break;
      case "referrer_note_created":
        setReferrerNotes((prev) => {
          if (prev.some((n) => n.id === msg.data.id)) return prev;
          return [msg.data, ...prev];
        });
        break;
      case "referrer_document_uploaded":
        toast.info(`Referrer uploaded: ${msg.data.original_filename || "a file"}`);
        break;
      default:
        break;
    }
  }, []);

  const wsToken = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  const { connected, peers, typingActors, sendTyping } = usePresenceWebSocket(
    caseId && wsToken ? `/api/ws/${caseId}?token=${wsToken}` : null,
    handleWsMessage,
  );

  const handleUpdateStage = async (stageKey, status) => {
    try {
      const { data } = await API.put(`/cases/${caseId}/stage`, { stage_key: stageKey, status });
      setCaseData(data);
      toast.success("Stage updated successfully");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleAddStage = async (label, afterStageKey) => {
    try {
      const { data } = await API.post(`/cases/${caseId}/stages`, { label, after_stage_key: afterStageKey });
      setCaseData(data);
      toast.success("Custom stage added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleRemoveStage = async (stageKey) => {
    try {
      const { data } = await API.delete(`/cases/${caseId}/stages/${stageKey}`);
      setCaseData(data);
      toast.success("Stage removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      // Honour active folder selection — if the user is currently viewing a
      // specific folder, new uploads land there. "all" / "uncategorised" /
      // null → root (no folder_id query param).
      const folderQS = (activeFolderId && activeFolderId !== "all" && activeFolderId !== "uncategorised")
        ? `&folder_id=${encodeURIComponent(activeFolderId)}` : "";
      if (files.length === 1) {
        const formData = new FormData();
        formData.append("file", files[0]);
        await API.post(`/cases/${caseId}/documents?category=${uploadCategory}${folderQS}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Document uploaded");
      } else {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        const { data } = await API.post(`/cases/${caseId}/documents/bulk?category=${uploadCategory}${folderQS}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${data.total_uploaded} document${data.total_uploaded !== 1 ? "s" : ""} uploaded${data.total_failed > 0 ? ` (${data.total_failed} failed)` : ""}`);
      }
      const { data } = await API.get(`/cases/${caseId}/documents`);
      setDocuments(data);
      // Refresh checklist
      API.get(`/cases/${caseId}/checklist`).then(({ data: cl }) => setChecklist(cl)).catch(() => {});
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUploadDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    await uploadFiles(files);
    e.target.value = "";
  };

  // -------- Folder handlers ----------

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error("Folder name required"); return; }
    setSavingFolder(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/folders`, { name, hidden_from_client: newFolderHidden });
      setFolders((prev) => [...prev, data]);
      setActiveFolderId(data.id);
      toast.success("Folder added");
      setFolderDialogOpen(false);
      setNewFolderName(""); setNewFolderHidden(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add folder");
    } finally {
      setSavingFolder(false);
    }
  };

  const handleRenameFolder = async (folderId, currentName) => {
    const next = window.prompt("Rename folder", currentName);
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === currentName) return;
    try {
      const { data } = await API.patch(`/cases/${caseId}/folders/${folderId}`, { name: trimmed });
      setFolders((prev) => prev.map((f) => f.id === folderId ? data : f));
      toast.success("Folder renamed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to rename");
    }
  };

  const handleToggleFolderHidden = async (folderId, currentHidden) => {
    try {
      const { data } = await API.patch(`/cases/${caseId}/folders/${folderId}`, { hidden_from_client: !currentHidden });
      setFolders((prev) => prev.map((f) => f.id === folderId ? data : f));
      toast.success(data.hidden_from_client ? "Hidden from client portal" : "Now visible to client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteFolder = async (folderId, name) => {
    if (!window.confirm(`Delete folder "${name}"? Documents inside will move back to the case root — they won't be deleted.`)) return;
    try {
      await API.delete(`/cases/${caseId}/folders/${folderId}`);
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      // Documents inside lose their folder_id — refetch to mirror server.
      const { data } = await API.get(`/cases/${caseId}/documents`);
      setDocuments(data);
      if (activeFolderId === folderId) setActiveFolderId("all");
      toast.success("Folder deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleMoveDocToFolder = async (docId, folderId) => {
    try {
      const { data } = await API.put(`/documents/${docId}/folder`, { folder_id: folderId || null });
      setDocuments((prev) => prev.map((d) => d.id === docId ? data : d));
      toast.success(folderId ? "Moved" : "Moved to root");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to move");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    await uploadFiles(files);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };

  const handleUpdateDocCategory = async (docId, category) => {
    try {
      await API.put(`/documents/${docId}/category`, { category });
      setDocuments((prev) => prev.map((d) => d.id === docId ? { ...d, category } : d));
      // Refresh checklist since uploaded categories changed
      API.get(`/cases/${caseId}/checklist`).then(({ data }) => setChecklist(data)).catch(() => {});
    } catch {
      toast.error("Failed to update category");
    }
  };

  const handleUpdateDocExpiry = async (docId, expiryDate) => {
    try {
      await API.put(`/documents/${docId}/expiry`, { expiry_date: expiryDate || null });
      setDocuments((prev) => prev.map((d) => d.id === docId ? { ...d, expiry_date: expiryDate || null } : d));
      toast.success(expiryDate ? "Expiry date set" : "Expiry date cleared");
    } catch {
      toast.error("Failed to update expiry date");
    }
  };

  // Solicitor-only: flip a document's pin state. Pinned docs float to the
  // top of the Documents tab in their own "Pinned" section.
  const handleTogglePin = async (doc) => {
    try {
      const { data } = await API.put(`/documents/${doc.id}/pin`, { pinned: !doc.pinned });
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? data : d)));
      toast.success(data.pinned ? "Pinned to top" : "Unpinned");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update pin");
    }
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { label: "Expired", color: "bg-red-100 text-red-700", icon: "expired" };
    if (daysLeft <= 30) return { label: `Expires ${new Date(expiryDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`, color: "bg-amber-100 text-amber-700", icon: "warning" };
    return { label: `Expires ${new Date(expiryDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`, color: "bg-slate-100 text-slate-500", icon: "ok" };
  };

  const handleUpdateChecklist = async (stageKey, items) => {
    try {
      await API.put(`/cases/${caseId}/checklist`, { stage_key: stageKey, items });
      const { data } = await API.get(`/cases/${caseId}/checklist`);
      setChecklist(data);
      toast.success("Checklist updated");
    } catch (err) {
      toast.error("Failed to update checklist");
    }
  };

  const handleAddSubcategory = async (stageKey, label) => {
    try {
      const { data } = await API.post(`/cases/${caseId}/stages/${stageKey}/subcategories`, { label });
      setCaseData(data);
      toast.success("Sub-task added");
    } catch (err) {
      toast.error("Failed to add sub-task");
    }
  };

  const handleToggleSubcategory = async (stageKey, subId) => {
    const stage = caseData?.stages?.find((s) => s.key === stageKey);
    const sub = stage?.subcategories?.find((x) => x.id === subId);
    // Guard: un-ticking a milestone on an already-completed stage will reopen
    // the stage — confirm before silently reverting its status.
    if (stage?.status === "completed" && sub?.completed) {
      setReopenGuard({ stageKey, subId, stageLabel: stage.label || stage.name || "this stage" });
      return;
    }
    await doToggleSubcategory(stageKey, subId);
  };

  const doToggleSubcategory = async (stageKey, subId) => {
    const wasCompleted = caseData?.stages?.find((s) => s.key === stageKey)?.status === "completed";
    try {
      const { data } = await API.put(`/cases/${caseId}/stages/${stageKey}/subcategories/${subId}/toggle`);
      setCaseData(data);
      const nowStage = data?.stages?.find((s) => s.key === stageKey);
      if (!wasCompleted && nowStage?.status === "completed") {
        toast.success(`All milestones ticked — "${nowStage.label}" marked complete automatically.`);
      } else if (wasCompleted && nowStage?.status !== "completed") {
        toast.info(`"${nowStage?.label || "Stage"}" reopened.`);
      }
    } catch (err) {
      toast.error("Failed to update sub-task");
    }
  };

  const handleDeleteSubcategory = async (stageKey, subId) => {
    try {
      const { data } = await API.delete(`/cases/${caseId}/stages/${stageKey}/subcategories/${subId}`);
      setCaseData(data);
      toast.success("Sub-task removed");
    } catch (err) {
      toast.error("Failed to remove sub-task");
    }
  };

  const handleUpdateSubcategory = async (stageKey, subId, payload) => {
    try {
      const { data } = await API.put(`/cases/${caseId}/stages/${stageKey}/subcategories/${subId}`, payload);
      setCaseData(data);
      toast.success("Agent note saved");
    } catch (err) {
      toast.error("Failed to save agent note");
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      await API.post(`/templates/from-case/${caseId}`, { name: templateName.trim(), description: templateDesc.trim() });
      setSaveTemplateOpen(false);
      setTemplateName("");
      setTemplateDesc("");
      toast.success("Workflow saved as template");
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId) return;
    setApplyingTemplate(true);
    try {
      const { data } = await API.put(`/cases/${caseId}/apply-template`, { template_id: selectedTemplateId });
      setCaseData(data);
      setApplyTemplateOpen(false);
      setSelectedTemplateId("");
      // Refresh checklist as template may change overrides
      API.get(`/cases/${caseId}/checklist`).then(({ data: cl }) => setChecklist(cl)).catch(() => {});
      toast.success("Template applied to case");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setApplyingTemplate(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      toast.info("Generating report...");
      const response = await API.get(`/cases/${caseId}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ConveyTrack_Report_${caseData?.title?.replace(/\s/g, "_").slice(0, 30)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error("Failed to generate report");
    }
  };

  // Open the "Send for signature" dialog with sensible defaults —
  // primary client's name/email if we have them, subject line derived
  // from the doc's filename.
  const openSigDialog = (doc) => {
    const client = (caseData?.clients || [])[0] || {};
    setSigDialog({
      open: true, doc,
      mode: "standard",
      signer_name: `${client.first_name || ""} ${client.last_name || ""}`.trim(),
      signer_email: client.email || "",
      signer_phone: client.phone || client.mobile || "",
      email_subject: `Please sign: ${doc.original_filename || doc.filename || "document"}`.slice(0, 100),
      email_blurb: "Please review and sign at your convenience.",
      include_witness: false,
      witness_name: "",
      witness_email: "",
      sending: false,
    });
  };

  const resetSigDialog = () => setSigDialog({ open: false, doc: null, mode: "standard", signer_name: "", signer_email: "", signer_phone: "", email_subject: "", email_blurb: "", include_witness: false, witness_name: "", witness_email: "", sending: false });

  // Deed template + test-deed state. Kept separate from the main
  // sig dialog so solicitors can generate a template into the case
  // and then send it in a separate step (or generate + save + open
  // the sig dialog straight after).
  const [testDeedDialog, setTestDeedDialog] = useState({ open: false, signer_phone: "", witness_name: "", witness_email: "", sending: false });
  const resetTestDeedDialog = () => setTestDeedDialog({ open: false, signer_phone: "", witness_name: "", witness_email: "", sending: false });

  const downloadDeedTemplate = async (form) => {
    try {
      const resp = await API.get(`/docusign/templates/${form}`, {
        params: { case_id: caseId, save_to_case: true },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${form} Template - ${(caseData?.property_address || caseId).slice(0, 40)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${form} template generated and filed into this case`);
      API.get(`/cases/${caseId}/documents`).then(({ data }) => setDocuments(data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate template");
    }
  };

  const handleSendTestDeed = async () => {
    const { signer_phone, witness_name, witness_email } = testDeedDialog;
    if (!signer_phone.trim() || !witness_name.trim() || !witness_email.trim()) {
      toast.error("Mobile, witness name and witness email are all required");
      return;
    }
    setTestDeedDialog((s) => ({ ...s, sending: true }));
    try {
      await API.post("/docusign/test-deed", {
        case_id: caseId,
        signer_phone: signer_phone.trim(),
        witness_name: witness_name.trim(),
        witness_email: witness_email.trim(),
        form: "TR1",
      });
      toast.success("Test deed sent — check your email + SMS");
      resetTestDeedDialog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to send test deed");
      setTestDeedDialog((s) => ({ ...s, sending: false }));
    }
  };

  const handleSendForSignature = async () => {
    const { doc, mode, signer_name, signer_email, signer_phone, email_subject, email_blurb, include_witness, witness_name, witness_email } = sigDialog;
    const isTr1 = mode === "tr1";
    if (!doc || !signer_email || !signer_name) {
      toast.error("Signer name and email are required");
      return;
    }
    if (isTr1) {
      // TR1 mode enforces the full HMLR Practice Guide 8 §13 stack —
      // client mobile for SMS auth + witness for in-person routing.
      // Non-negotiable, so we validate before hitting the API.
      if (!signer_phone.trim()) {
        toast.error("Signer's UK mobile number is required for HMLR-compliant deed signing");
        return;
      }
      if (!witness_name.trim() || !witness_email.trim()) {
        toast.error("HMLR-compliant deeds require a witness — name and email are mandatory");
        return;
      }
      if (witness_email.trim().toLowerCase() === signer_email.trim().toLowerCase()) {
        toast.error("Witness email must differ from signer email");
        return;
      }
    } else if (include_witness) {
      // Standard mode with optional witness (contracts occasionally
      // need one but it's not HMLR-compliant — witness gets emailed
      // after client signs, no SMS auth).
      if (!witness_name.trim() || !witness_email.trim()) {
        toast.error("Witness name and email are required");
        return;
      }
      if (witness_email.trim().toLowerCase() === signer_email.trim().toLowerCase()) {
        toast.error("Witness email must differ from signer email");
        return;
      }
    }
    setSigDialog((s) => ({ ...s, sending: true }));
    try {
      const resp = await API.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const pdf_base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onerror = rej;
        r.onload = () => res(String(r.result).split(",")[1] || "");
        r.readAsDataURL(resp.data);
      });
      const includeWitnessOnWire = isTr1 || include_witness;
      await API.post("/docusign/envelopes", {
        case_id: caseId, doc_id: doc.id, signer_name, signer_email,
        email_subject, email_blurb, pdf_base64,
        pdf_name: doc.original_filename || doc.filename || "document.pdf",
        witness_name: includeWitnessOnWire ? witness_name.trim() : "",
        witness_email: includeWitnessOnWire ? witness_email.trim() : "",
        tr1_mode: isTr1,
        signer_phone: isTr1 ? signer_phone.trim() : "",
      });
      toast.success(isTr1
        ? `HMLR deed sent to ${signer_email} · witness ${witness_email} · PG82 will auto-generate on completion`
        : includeWitnessOnWire
          ? `Envelope sent to ${signer_email} → ${witness_email}`
          : `Envelope sent to ${signer_email}`);
      resetSigDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to send envelope");
      setSigDialog((s) => ({ ...s, sending: false }));
    }
  };

  const handleConvertToPdf = async (doc) => {
    setConvertingDocId(doc.id);
    try {
      const { data } = await API.post(`/cases/${caseId}/documents/${doc.id}/convert-to-pdf`);
      setDocuments((prev) => prev.some((d) => d.id === data.id) ? prev : [data, ...prev]);
      toast.success(`Created ${data.original_filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not convert to PDF");
    } finally {
      setConvertingDocId(null);
    }
  };

  const handleDownloadDoc = async (doc) => {
    // Opens the document in the user's default viewer / app instead of
    // forcing a "Save As" dialog. Strategy:
    //   • PDFs / images / text → open inline in a new browser tab (the
    //     browser uses its native PDF viewer / image renderer).
    //   • Office docs (.docx/.xlsx/.pptx) → fetch as blob, open in new
    //     tab with the correct MIME so the browser prompts to open in
    //     Word / Excel / PowerPoint (whichever the OS associates).
    //   • Anything else → fall back to traditional download.
    const filename = doc.original_filename || doc.filename || "document";
    const ext = filename.split(".").pop()?.toLowerCase() || "";
    const inlineMimes = {
      pdf: "application/pdf",
      txt: "text/plain",
      csv: "text/csv",
      json: "application/json",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
      heic: "image/heic",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    };
    try {
      const response = await API.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const mime = doc.mime_type || inlineMimes[ext] || "application/octet-stream";
      const blob = new Blob([response.data], { type: mime });
      const url = URL.createObjectURL(blob);

      // PDFs + images + text → open the blob in a new tab; browser
      // handles preview. Office files → open in new tab too, which
      // triggers the OS handler (Word / Excel) on most browsers.
      const openInline = ext in inlineMimes;
      if (openInline) {
        const win = window.open(url, "_blank", "noopener");
        if (!win) {
          // Pop-up blocked — fall through to download so user isn't stuck.
          const a = document.createElement("a");
          a.href = url; a.download = filename;
          document.body.appendChild(a); a.click(); a.remove();
        }
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
      }
      // Revoke after a delay so the new tab has loaded.
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setCreatingTask(true);
    try {
      const taskPayload = {
        title: newTask.title,
        description: newTask.description,
        assigned_to: newTask.assigned_to || null,
        due_date: calendarDate ? format(calendarDate, "yyyy-MM-dd") : null,
      };
      await API.post(`/cases/${caseId}/tasks`, taskPayload);
      toast.success("Task created");
      setTaskDialogOpen(false);
      setNewTask({ title: "", description: "", due_date: "", assigned_to: "" });
      setCalendarDate(undefined);
      const { data } = await API.get(`/cases/${caseId}/tasks`);
      setTasks(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreatingTask(false);
    }
  };

  const handleToggleTask = async (task) => {
    try {
      const newStatus = task.status === "completed" ? "pending" : "completed";
      await API.put(`/tasks/${task.id}`, { status: newStatus });
      const { data } = await API.get(`/cases/${caseId}/tasks`);
      setTasks(data);
    } catch (err) {
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/tasks/${task.id}`);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      toast.success("Task deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete task");
    }
  };

  const handleDeleteDocument = async (doc) => {
    const name = doc.original_filename || doc.filename || "this document";
    if (!window.confirm(`Delete ${name}? It will be removed from this case.`)) return;
    try {
      await API.delete(`/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success("Document deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete document");
    }
  };

  // ---------- Bulk select for documents + tasks ----------
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const toggleDocSel = (id) => {
    const n = new Set(selectedDocs); n.has(id) ? n.delete(id) : n.add(id); setSelectedDocs(n);
  };
  const toggleTaskSel = (id) => {
    const n = new Set(selectedTasks); n.has(id) ? n.delete(id) : n.add(id); setSelectedTasks(n);
  };
  const handleBulkDeleteDocs = async () => {
    if (!selectedDocs.size) return;
    if (!window.confirm(`Delete ${selectedDocs.size} document${selectedDocs.size === 1 ? "" : "s"}?`)) return;
    try {
      const { data } = await API.post(`/cases/${caseId}/documents/bulk-delete`, { ids: Array.from(selectedDocs) });
      toast.success(`${data.deleted} document${data.deleted === 1 ? "" : "s"} deleted`);
      setDocuments((prev) => prev.filter((d) => !selectedDocs.has(d.id)));
      setSelectedDocs(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };
  // Streams a single .zip with the selected docs back to the browser.
  // We post (not GET) so the id list can be arbitrarily large without
  // hitting URL-length limits.
  const handleBulkDownloadDocs = async () => {
    if (!selectedDocs.size) return;
    const count = selectedDocs.size;
    // Persistent loading toast — bulk ZIPs can take several seconds for
    // larger cases, so we keep a spinner visible until the blob arrives
    // and then mutate the same toast into a success/error message.
    const toastId = toast.loading(
      `Preparing ${count} document${count === 1 ? "" : "s"} for download…`,
    );
    try {
      const response = await API.post(
        `/cases/${caseId}/documents/bulk-download`,
        { ids: Array.from(selectedDocs) },
        { responseType: "blob" },
      );
      // Filename comes from the server's Content-Disposition; fall
      // back to a generic name when the header is stripped by a proxy.
      let filename = `case-${caseId}-documents.zip`;
      const cd = response.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m) filename = m[1];
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${count} document${count === 1 ? "" : "s"}`, { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk download failed", { id: toastId });
    }
  };
  // Bulk move/copy open a folder picker; `bulkFolderAction` holds "move"|"copy".
  const handleBulkMoveDocs = () => { if (selectedDocs.size) setBulkFolderAction("move"); };
  const handleBulkCopyDocs = () => { if (selectedDocs.size) setBulkFolderAction("copy"); };
  const runBulkFolderAction = async (folderId) => {
    const ids = Array.from(selectedDocs);
    const action = bulkFolderAction;
    setBulkFolderAction(null);
    if (!ids.length || !action) return;
    try {
      const { data } = await API.post(`/cases/${caseId}/documents/bulk-${action}`, { ids, folder_id: folderId });
      if (action === "move") {
        setDocuments((prev) => prev.map((d) => ids.includes(d.id) ? { ...d, folder_id: folderId || undefined } : d));
        toast.success(`${data.moved} document${data.moved === 1 ? "" : "s"} moved`);
      } else {
        setDocuments((prev) => [...(data.documents || []), ...prev]);
        toast.success(`${data.copied} document${data.copied === 1 ? "" : "s"} copied`);
      }
      setSelectedDocs(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || `Bulk ${action} failed`);
    }
  };
  const handleBulkDeleteTasks = async () => {
    if (!selectedTasks.size) return;
    if (!window.confirm(`Delete ${selectedTasks.size} task${selectedTasks.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const { data } = await API.post(`/cases/${caseId}/tasks/bulk-delete`, { ids: Array.from(selectedTasks) });
      toast.success(`${data.deleted} task${data.deleted === 1 ? "" : "s"} deleted`);
      setTasks((prev) => prev.filter((t) => !selectedTasks.has(t.id)));
      setSelectedTasks(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };

  const uploadMessageAttachment = async (channel, files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    const setUp = channel === "referrer" ? setUploadingReferrerFile : setUploadingNoteFile;
    const setFiles = channel === "referrer" ? setReferrerNoteFiles : setNoteFiles;
    setUp(true);
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await API.post(`/cases/${caseId}/message-attachments?channel=${channel}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if ((file.type || "").startsWith("image/")) data.preview = URL.createObjectURL(file);
        setFiles((p) => [...p, data]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed: ${file.name}`);
      }
    }
    setUp(false);
  };

  const attachFromDocument = async (channel, doc) => {
    const setFiles = channel === "referrer" ? setReferrerNoteFiles : setNoteFiles;
    try {
      const { data } = await API.post(`/cases/${caseId}/message-attachments/from-document`, {
        document_id: doc.id, channel,
      });
      setFiles((p) => [...p, data]);
      toast.success(`${doc.original_filename} attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to attach document");
    }
  };

  const removePendingAttachment = async (channel, attId) => {
    const setFiles = channel === "referrer" ? setReferrerNoteFiles : setNoteFiles;
    setFiles((p) => {
      const found = p.find((a) => a.id === attId);
      if (found?.preview) URL.revokeObjectURL(found.preview);
      return p.filter((a) => a.id !== attId);
    });
    try { await API.delete(`/message-attachments/${attId}`); } catch { /* best-effort cleanup */ }
  };

  const deleteSentAttachment = async (channel, noteId, attId) => {
    try {
      await API.delete(`/message-attachments/${attId}`);
      const setter = channel === "referrer" ? setReferrerNotes : setNotes;
      setter((prev) => prev.map((n) => n.id === noteId
        ? { ...n, attachments: (n.attachments || []).filter((a) => a.id !== attId) }
        : n));
      toast.success("Attachment deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete attachment");
    }
  };

  const handleSendNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim() && noteFiles.length === 0) return;
    setSendingNote(true);
    try {
      await API.post(`/cases/${caseId}/notes`, {
        content: noteText.trim(),
        attachment_ids: noteFiles.map((a) => a.id),
      });
      setNoteText("");
      setNoteFiles([]);
      const { data } = await API.get(`/cases/${caseId}/notes`);
      setNotes(data);
    } catch (err) {
      toast.error("Failed to send note");
    } finally {
      setSendingNote(false);
    }
  };

  const handleSendReferrerNote = async (e) => {
    e.preventDefault();
    if (!referrerNoteText.trim() && referrerNoteFiles.length === 0) return;
    setSendingReferrerNote(true);
    try {
      await API.post(`/referrer-notes`, {
        content: referrerNoteText.trim(),
        case_id: caseId,
        attachment_ids: referrerNoteFiles.map((a) => a.id),
      });
      setReferrerNoteText("");
      setReferrerNoteFiles([]);
      const { data } = await API.get(`/referrer-notes/${caseId}`);
      setReferrerNotes(data);
    } catch (err) {
      toast.error("Failed to send referrer message");
    } finally {
      setSendingReferrerNote(false);
    }
  };

  const loadMessageBlob = async (attId) => {
    const resp = await API.get(`/message-attachments/${attId}/download`, { responseType: "blob" });
    return resp.data;
  };

  const collectImages = (noteList) =>
    noteList.flatMap((n) => (n.attachments || []).filter((a) => (a.content_type || "").startsWith("image/")));

  const handleChatPaste = (channel) => (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items.filter((it) => it.kind === "file" && it.type.startsWith("image/")).map((it) => it.getAsFile()).filter(Boolean);
    if (files.length) {
      e.preventDefault();
      uploadMessageAttachment(channel, files);
    }
  };

  // Drag-and-drop file attach onto a chat panel. `channel` is "client" or
  // "referrer"; only reacts to real file drags (not text/element drags).
  const makeChatDrag = (channel) => ({
    onDragEnter: (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault(); e.stopPropagation();
      dragDepthRef.current += 1;
      setDragChannel(channel);
    },
    onDragOver: (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault(); e.stopPropagation();
    },
    onDragLeave: (e) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault(); e.stopPropagation();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDragChannel(null);
    },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation();
      dragDepthRef.current = 0;
      setDragChannel(null);
      if (e.dataTransfer?.files?.length) uploadMessageAttachment(channel, e.dataTransfer.files);
    },
  });

  const getProgress = (stages) => getCaseProgress(stages);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50"><Header />
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-slate-50"><Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <p className="text-slate-500">Case not found</p>
          <Button variant="link" onClick={() => navigate("/")} className="mt-2 text-blue-600">Go back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="case-detail-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Top bar */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate(-1)} data-testid="back-btn"
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {caseData.title}
              </h1>
              {isSolicitor && (
                <button
                  type="button"
                  onClick={() => setEditCaseHeaderOpen(true)}
                  data-testid="edit-case-header-btn"
                  title="Edit case name & property address"
                  className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <PencilSimple weight="bold" className="w-3.5 h-3.5" />
                </button>
              )}
              <span className={`shrink-0 text-xs font-medium px-2.5 py-0.5 rounded-full ${caseData.case_type === "buying" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                {caseData.case_type}
              </span>
              {isSolicitor && (caseData.case_type === "selling" || caseData.transaction_type === "sale") && (
                <button
                  type="button"
                  onClick={() => navigate(`/cases/${caseData.id}/ta13`)}
                  data-testid="open-ta13-btn"
                  title="Prepare TA13 (Completion Information)"
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                >
                  <FileText weight="bold" className="w-3 h-3" />
                  TA13
                </button>
              )}
              {caseData.linked_case_id && (
                <button
                  type="button"
                  onClick={() => navigate(`/cases/${caseData.linked_case_id}`)}
                  data-testid="linked-case-badge"
                  title="Open the paired sale/purchase case"
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition-colors"
                >
                  <LinkSimple weight="bold" className="w-3 h-3" />
                  {caseData.transaction_leg === "sale" ? "Open paired Purchase" : caseData.transaction_leg === "purchase" ? "Open paired Sale" : "Open paired case"}
                </button>
              )}
              {/* Inline-editable case ref. Solicitors click to edit; clients see
                  the pill read-only. Empty state still renders a "+ Add ref"
                  button so it's discoverable on every new case. */}
              <CaseRefPill
                caseId={caseData.id}
                value={caseData.case_ref}
                isSolicitor={isSolicitor}
                onSaved={(newRef) => setCaseData((prev) => ({ ...prev, case_ref: newRef }))}
              />
              {/* Per-case email reference — copy to paste into Outlook so
                  out-of-app replies auto-file back to this case. */}
              {isSolicitor && <EmailRefPill autoRef={caseData.auto_ref} />}
              {/* Live indicator */}
              <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${connected ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}
                data-testid="ws-status">
                {connected ? <WifiHigh weight="bold" className="w-3 h-3" /> : <WifiSlash weight="bold" className="w-3 h-3" />}
                {connected ? "Live" : "Offline"}
              </span>
            </div>
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
              <MapPin weight="fill" className="w-3.5 h-3.5" />{caseData.property_address}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            {isSolicitor && (
              <button
                onClick={() => setAiDraftOpen(true)}
                data-testid="case-ai-draft-btn"
                title="Draft letters, summaries and clauses with AI (grounded on this case)"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-gradient-to-r from-[#100f5d] to-[#2a2896] text-white text-xs font-medium hover:opacity-90 transition-opacity"
              >
                <Sparkle weight="fill" className="w-3.5 h-3.5" /> Draft with AI
              </button>
            )}
            {isSolicitor && (
              <CaseExportMenu
                caseId={caseData.id}
                caseRef={caseData.case_ref}
                caseTitle={caseData.title}
              />
            )}
            {isSolicitor && (
              <div className="flex items-center gap-1">
                <button onClick={handleArchiveCase} disabled={archivingCase}
                  data-testid="case-archive-btn"
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
                    caseData.status === "archived"
                      ? "border-emerald-200 hover:bg-emerald-50 text-emerald-600"
                      : "border-slate-200 hover:bg-amber-50 text-slate-400 hover:text-amber-600"
                  }`}
                  title={caseData.status === "archived" ? "Restore case" : "Archive case"}>
                  <Archive weight="bold" className="w-4 h-4" />
                </button>
                {caseData.status !== "archived" && (
                  <button onClick={() => setCancelDialogOpen(true)}
                    data-testid="case-cancel-btn"
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                    title="Cancel case (deal fell through — no referral fee)">
                    <Prohibit weight="bold" className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => setDeleteDialogOpen(true)}
                  data-testid="case-delete-btn"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                  title="Delete case">
                  <Trash weight="bold" className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="text-right">
              <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{getProgress(caseData.stages)}%</span>
              <p className="text-xs text-slate-500">complete</p>
            </div>
          </div>
        </div>
        {isSolicitor && (
          <AiDraftDialog caseId={caseData.id} open={aiDraftOpen} onOpenChange={setAiDraftOpen} />
        )}

        {/* Archived / Cancelled Banner */}
        {caseData.status === "archived" && (
          <div className={`mb-4 p-3 border rounded-lg flex items-center justify-between ${caseData.cancelled_at ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`} data-testid="archived-banner">
            <div className="flex items-start gap-2 min-w-0">
              {caseData.cancelled_at ? (
                <Prohibit weight="fill" className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
              ) : (
                <Archive weight="fill" className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              )}
              <div className="min-w-0">
                <p className={`text-sm font-medium ${caseData.cancelled_at ? "text-rose-700" : "text-amber-700"}`}>
                  {caseData.cancelled_at ? "This case has been cancelled" : "This case is archived"}
                </p>
                {caseData.cancelled_at && caseData.cancellation_reason && (
                  <p className="text-xs text-rose-600 mt-0.5 truncate"><span className="font-semibold">Reason:</span> {caseData.cancellation_reason}</p>
                )}
              </div>
            </div>
            {isSolicitor && (
              <Button variant="outline" onClick={handleArchiveCase} disabled={archivingCase}
                data-testid="restore-case-btn"
                className={`h-7 text-xs shrink-0 ${caseData.cancelled_at ? "border-rose-300 text-rose-700 hover:bg-rose-100" : "border-amber-300 text-amber-700 hover:bg-amber-100"}`}>
                {archivingCase ? "Restoring..." : "Restore Case"}
              </Button>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {/* Folder Add dialog — used by the Documents tab folder bar. */}
        <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>New Folder</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Folder name</Label>
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && newFolderName.trim()) handleCreateFolder(); }}
                  placeholder="e.g. Lender enquiries"
                  maxLength={60}
                  autoFocus
                  data-testid="new-folder-name-input"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-600 select-none">
                <input
                  type="checkbox"
                  checked={newFolderHidden}
                  onChange={(e) => setNewFolderHidden(e.target.checked)}
                  className="mt-0.5"
                  data-testid="new-folder-hidden-checkbox"
                />
                <span>
                  <strong>Internal only</strong> — hide this folder and its docs from the client portal.
                </span>
              </label>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => { setFolderDialogOpen(false); setNewFolderName(""); setNewFolderHidden(false); }} className="flex-1" data-testid="cancel-folder-btn">
                  Cancel
                </Button>
                <Button onClick={handleCreateFolder} disabled={savingFolder || !newFolderName.trim()} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-folder-btn">
                  {savingFolder ? "Adding..." : "Add Folder"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Case</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700 font-medium flex items-center gap-1.5">
                  <Warning weight="fill" className="w-4 h-4" /> Case will be moved to Recently Deleted
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  You'll have 30 days to restore it before it's permanently removed.
                </p>
              </div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm font-medium text-slate-900">{caseData.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{caseData.property_address}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1" data-testid="cancel-delete-btn">
                  Cancel
                </Button>
                <Button onClick={handleDeleteCase} disabled={deletingCase}
                  data-testid="confirm-delete-case-btn"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                  {deletingCase ? "Deleting..." : "Delete Case"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Case Dialog — distinct from Archive: clears unpaid referrer
            fee and posts a referrer note explaining why. */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="cancel-case-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Cancel Case</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
                <p className="text-sm text-rose-700 font-medium flex items-center gap-1.5">
                  <Prohibit weight="fill" className="w-4 h-4" /> Mark this deal as cancelled
                </p>
                <ul className="text-xs text-rose-600 mt-1.5 space-y-0.5 list-disc list-inside">
                  <li>Case status set to <span className="font-semibold">archived</span> — leaves active workflows.</li>
                  {caseData?.referrer_id && !caseData?.referrer_fee_paid && caseData?.referral_fee > 0 && (
                    <li>Unpaid referral fee of <span className="font-semibold">£{Number(caseData.referral_fee).toLocaleString()}</span> will be removed.</li>
                  )}
                  {caseData?.referrer_id && (
                    <li>Auto-posts a note on the referrer's portal explaining the cancellation.</li>
                  )}
                  {caseData?.referrer_fee_paid && (
                    <li>Referrer fees already paid stay recorded — that's earned revenue.</li>
                  )}
                </ul>
              </div>
              <div>
                <Label className="text-xs font-medium">Reason</Label>
                <Select value={cancelCategory || "__none__"} onValueChange={(v) => setCancelCategory(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm" data-testid="cancel-category-select">
                    <SelectValue placeholder="Pick a reason…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" data-testid="cancel-cat-none">— Not specified —</SelectItem>
                    <SelectItem value="buyer_pulled_out" data-testid="cancel-cat-buyer_pulled_out">Buyer pulled out</SelectItem>
                    <SelectItem value="seller_pulled_out" data-testid="cancel-cat-seller_pulled_out">Seller pulled out</SelectItem>
                    <SelectItem value="chain_collapsed" data-testid="cancel-cat-chain_collapsed">Chain collapsed</SelectItem>
                    <SelectItem value="survey_issues" data-testid="cancel-cat-survey_issues">Survey issues</SelectItem>
                    <SelectItem value="title_issue" data-testid="cancel-cat-title_issue">Title issue</SelectItem>
                    <SelectItem value="funding_issue" data-testid="cancel-cat-funding_issue">Funding issue</SelectItem>
                    <SelectItem value="mutual_withdrawal" data-testid="cancel-cat-mutual_withdrawal">Mutual withdrawal</SelectItem>
                    <SelectItem value="other" data-testid="cancel-cat-other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cancel-reason" className="text-xs font-medium">Notes (optional, shown to referrer)</Label>
                <Input
                  id="cancel-reason"
                  data-testid="cancel-reason-input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="e.g. Survey flagged subsidence; client withdrew offer"
                  maxLength={200}
                  className="mt-1.5"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setCancelDialogOpen(false); setCancelCategory(""); setCancelReason(""); }} className="flex-1" data-testid="cancel-cancel-btn">
                  Keep case
                </Button>
                <Button onClick={handleCancelCase} disabled={cancellingCase}
                  data-testid="confirm-cancel-case-btn"
                  className="flex-1 bg-rose-600 hover:bg-rose-700 text-white">
                  {cancellingCase ? "Cancelling..." : "Cancel case"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 w-full sm:w-auto grid grid-cols-2 sm:flex h-auto">
            <TabsTrigger value="timeline" data-testid="tab-timeline" className="gap-1.5 text-xs sm:text-sm"><Clock weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Timeline</span></TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="gap-1.5 text-xs sm:text-sm">
              <FileText weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Docs ({documents.length})</span>
              {isSolicitor && (() => {
                const today = new Date(); today.setHours(0,0,0,0);
                const expCount = documents.filter(d => d.expiry_date && new Date(d.expiry_date) <= new Date(today.getTime() + 30*24*60*60*1000)).length;
                return expCount > 0 ? (
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold" data-testid="expiring-docs-badge">{expCount}</span>
                ) : null;
              })()}
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks" className="gap-1.5 text-xs sm:text-sm"><ListChecks weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Tasks ({tasks.length})</span></TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes" className="gap-1.5 text-xs sm:text-sm"><ChatCircle weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Notes ({notes.length + referrerNotes.length})</span></TabsTrigger>
            <TabsTrigger value="forms" data-testid="tab-forms" className="gap-1.5 text-xs sm:text-sm"><ClipboardText weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Forms</span></TabsTrigger>
            {isSolicitor && (
              <TabsTrigger value="communications" data-testid="tab-communications" className="gap-1.5 text-xs sm:text-sm"><EnvelopeSimple weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Communications</span></TabsTrigger>
            )}
            {isSolicitor && (
              <TabsTrigger value="accounts" data-testid="tab-accounts" className="gap-1.5 text-xs sm:text-sm"><Scales weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Accounts</span></TabsTrigger>
            )}
            {isSolicitor && (
              <TabsTrigger value="authorities" data-testid="tab-authorities" className="gap-1.5 text-xs sm:text-sm"><Stamp weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Authorities</span></TabsTrigger>
            )}
            {isSolicitor && (
              <TabsTrigger value="onboarding" data-testid="tab-onboarding" className="gap-1.5 text-xs sm:text-sm"><ClipboardText weight="bold" className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> <span className="truncate">Onboarding</span></TabsTrigger>
            )}
          </TabsList>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Conveyancing Progress</h3>
                <ConveyancingTimeline
                  stages={caseData.stages}
                  onUpdateStage={isSolicitor ? handleUpdateStage : undefined}
                  onAddStage={isSolicitor ? handleAddStage : undefined}
                  onRemoveStage={isSolicitor ? handleRemoveStage : undefined}
                  isSolicitor={isSolicitor}
                  checklist={checklist}
                  onUpdateChecklist={isSolicitor ? handleUpdateChecklist : undefined}
                  onAddSubcategory={isSolicitor ? handleAddSubcategory : undefined}
                  onToggleSubcategory={handleToggleSubcategory}
                  onDeleteSubcategory={isSolicitor ? handleDeleteSubcategory : undefined}
                  onUpdateSubcategory={isSolicitor ? handleUpdateSubcategory : undefined}
                />
                {/* Template actions */}
                {isSolicitor && (
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-200">
                    {user?.is_admin && (
                      <Button
                        variant="outline"
                        onClick={() => setSaveTemplateOpen(true)}
                        data-testid="save-template-btn"
                        className="h-8 text-xs border-slate-200 gap-1.5"
                      >
                        <FloppyDisk weight="bold" className="w-3.5 h-3.5" /> Save as Template
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        setApplyTemplateOpen(true);
                        API.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
                      }}
                      data-testid="apply-template-btn"
                      className="h-8 text-xs border-slate-200 gap-1.5"
                    >
                      <StackSimple weight="bold" className="w-3.5 h-3.5" /> Apply Template
                    </Button>
                  </div>
                )}

                {/* Save as Template Dialog */}
                <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Save Workflow as Template</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-500">Save the current milestones, sub-tasks, and document checklists as a reusable template.</p>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Template Name</Label>
                        <Input
                          data-testid="template-name-input"
                          placeholder="e.g., Standard Purchase Workflow"
                          value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Description (optional)</Label>
                        <Textarea
                          data-testid="template-desc-input"
                          placeholder="Brief description of this workflow..."
                          value={templateDesc}
                          onChange={(e) => setTemplateDesc(e.target.value)}
                          rows={2}
                        />
                      </div>
                      <Button
                        onClick={handleSaveAsTemplate}
                        disabled={savingTemplate || !templateName.trim()}
                        data-testid="confirm-save-template-btn"
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                      >
                        {savingTemplate ? "Saving..." : "Save Template"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Reopen-completed-stage confirmation */}
                <AlertDialog open={!!reopenGuard} onOpenChange={(o) => { if (!o) setReopenGuard(null); }}>
                  <AlertDialogContent data-testid="reopen-stage-guard-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Reopen completed stage?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Un-ticking this milestone will reopen the completed stage
                        {reopenGuard?.stageLabel ? <> “<span className="font-semibold">{reopenGuard.stageLabel}</span>”</> : ""} and
                        revert its status to in-progress. Continue?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="reopen-stage-cancel-btn">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="reopen-stage-confirm-btn"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => {
                          const g = reopenGuard;
                          setReopenGuard(null);
                          if (g) doToggleSubcategory(g.stageKey, g.subId);
                        }}
                      >
                        Reopen stage
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Dialog open={applyTemplateOpen} onOpenChange={setApplyTemplateOpen}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Apply Template to Case</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-amber-600 flex items-center gap-1.5">
                      <Warning weight="fill" className="w-4 h-4" />
                      This will replace the current workflow stages.
                    </p>
                    <div className="space-y-4">
                      {templates.length === 0 ? (
                        <p className="text-sm text-slate-400 py-4 text-center">No templates available. Save a workflow as a template first.</p>
                      ) : (
                        <>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {templates.map((t) => (
                              <div key={t.id}
                                onClick={() => setSelectedTemplateId(t.id)}
                                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                  selectedTemplateId === t.id ? "border-blue-300 bg-blue-50/50" : "border-slate-200 hover:border-slate-300"
                                }`}
                                data-testid={`template-option-${t.id}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <StackSimple weight="fill" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                    <p className="text-sm font-medium text-slate-900 truncate">{t.name}</p>
                                    <span className="text-[10px] text-slate-400 shrink-0">{t.stages?.length || 0} stages</span>
                                  </div>
                                  {t.description && <p className="text-xs text-slate-500 mt-0.5 truncate pl-5">{t.description}</p>}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Delete template "${t.name}"?`)) {
                                      API.delete(`/templates/${t.id}`).then(() => {
                                        setTemplates((prev) => prev.filter((x) => x.id !== t.id));
                                        if (selectedTemplateId === t.id) setSelectedTemplateId("");
                                        toast.success("Template deleted");
                                      }).catch(() => toast.error("Failed to delete template"));
                                    }
                                  }}
                                  data-testid={`delete-template-${t.id}`}
                                  hidden={!user?.is_admin}
                                  className={`w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors shrink-0 ml-2 ${!user?.is_admin ? "hidden" : ""}`}
                                  title="Delete template (admin only)"
                                >
                                  <Trash weight="bold" className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                          {selectedTemplateId && (() => {
                            const tmpl = templates.find((t) => t.id === selectedTemplateId);
                            if (!tmpl) return null;
                            return (
                              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs space-y-1" data-testid="template-preview">
                                <p className="font-medium text-slate-700">{tmpl.stages?.length || 0} stages:</p>
                                <div className="flex flex-wrap gap-1">
                                  {tmpl.stages?.map((s) => (
                                    <span key={s.key} className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600 text-[10px] font-medium">
                                      {s.label}
                                      {s.subcategories?.length > 0 && (
                                        <span className="text-slate-400">({s.subcategories.length})</span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                          <Button
                            onClick={handleApplyTemplate}
                            disabled={applyingTemplate || !selectedTemplateId}
                            data-testid="confirm-apply-template-btn"
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                          >
                            {applyingTemplate ? "Applying..." : "Apply Template"}
                          </Button>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="lg:col-span-4 space-y-4">
                {isSolicitor && (
                  <PostCompletionSummaryCard caseId={caseId} onJump={() => setActiveTab("post-completion")} />
                )}
                {/* Solicitor Contact Card */}
                <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="solicitor-card">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Solicitor</h4>
                    {isSolicitor && (
                      <button onClick={() => setReassignDialogOpen(true)} data-testid="reassign-btn"
                        className="text-[10px] font-medium text-blue-600 hover:underline flex items-center gap-0.5">
                        <ArrowsClockwise weight="bold" className="w-3 h-3" /> Reassign
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center shrink-0">
                      <span className="text-white text-sm font-bold">{caseData.solicitor_name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{caseData.solicitor_name}</p>
                      <p className="text-[10px] text-blue-600 font-medium" data-testid="solicitor-user-type">
                        {caseData.solicitor_user_type === "client_onboarding" ? "Client Onboarding" :
                         caseData.solicitor_user_type === "completion_team" ? "Completion Team" : "Solicitor"}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs">
                    {caseData.solicitor_email && (
                      <a href={`mailto:${caseData.solicitor_email}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors" data-testid="solicitor-email">
                        <EnvelopeSimple weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{caseData.solicitor_email}</span>
                      </a>
                    )}
                    {caseData.solicitor_phone && (
                      <a href={`tel:${caseData.solicitor_phone}`} className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors" data-testid="solicitor-phone">
                        <Phone weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>{caseData.solicitor_phone}</span>
                      </a>
                    )}
                    {caseData.solicitor_office && (
                      <div className="flex items-center gap-2 text-slate-600" data-testid="solicitor-office">
                        <MapTrifold weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>{caseData.solicitor_office}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reassign Dialog */}
                <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Reassign Case</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-slate-500">Transfer this case to a different solicitor. All parties will be notified.</p>
                      <Select value={selectedSolicitorId} onValueChange={setSelectedSolicitorId}>
                        <SelectTrigger data-testid="reassign-solicitor-select"><SelectValue placeholder="Select solicitor" /></SelectTrigger>
                        <SelectContent>
                          {solicitors.filter(s => s.id !== caseData.solicitor_id).map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              <span className="flex items-center gap-2">
                                <UserCircle weight="fill" className="w-4 h-4 text-slate-400 inline" />
                                {s.name} <span className="text-xs text-slate-400">({s.email})</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleReassignCase} disabled={reassigning || !selectedSolicitorId}
                        data-testid="confirm-reassign-btn" className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                        {reassigning ? "Reassigning..." : "Reassign Case"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Case Details */}
                <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
                  <h4 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Case Details</h4>
                  <dl className="space-y-3">
                    {[
                      { label: "Type", value: caseData.case_type },
                      { label: "Status", value: caseData.status },
                      { label: "Created", value: new Date(caseData.created_at).toLocaleDateString("en-GB") },
                    ].map((item) => (
                      <div key={item.label}>
                        <dt className="text-xs text-slate-500 font-medium">{item.label}</dt>
                        <dd className="text-sm text-slate-900 font-medium capitalize mt-0.5">{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                {/* Property details — sticky right-column card showing
                    address / title number / tenure / price. Solicitors get
                    a click-to-edit Pencil; clients see read-only. Sits
                    above PartiesCard so it's the first thing visible. */}
                <PropertyDetailsCard
                  caseData={caseData}
                  isSolicitor={isSolicitor}
                  onUpdated={(updated) => setCaseData(updated)}
                />

                {/* Manual Title Risk — solicitor-driven risk score based on
                    checkbox-entered register indicators. Interim tool until
                    HMLR Register Extract Service is live. */}
                {isSolicitor && (
                  <ManualTitleRiskCard caseId={caseId} isSolicitor={isSolicitor} />
                )}

                {/* HMLR Business Gateway — direct integration for OC1
                    (and later OS1/K16/etc.) */}
                {isSolicitor && (
                  <HmlrRequestsCard caseId={caseId} isSolicitor={isSolicitor} />
                )}

                {/* Other parties — buyer/seller other side, their solicitor,
                    estate agent, surveyor, etc. Always visible (clients see
                    it read-only so they know who else is in the chain). */}
                <PartiesCard caseId={caseId} isSolicitor={isSolicitor} />

                {/* Conveyancing fees ledger — purchase price/SDLT/legal fees/
                    disbursements/3rd-party/money-in with per-row VAT, "add to
                    completion" + "add to invoice" toggles, and PDF export. */}
                {isSolicitor && (
                  <ConveyancingFeesCard caseId={caseId} isSolicitor={isSolicitor} />
                )}

                {/* Referrer Assignment */}
                {isSolicitor && (
                  <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="referrer-section">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <User weight="bold" className="w-4 h-4 text-violet-500" /> Referrer
                    </h4>
                    <Select value={caseData.referrer_id || "none"} onValueChange={(v) => handleAssignReferrer(v === "none" ? null : v)}>
                      <SelectTrigger data-testid="referrer-select" className="h-9 text-xs" disabled={assigningReferrer}>
                        <SelectValue placeholder="No referrer assigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No referrer</SelectItem>
                        {referrers.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}{r.company ? ` (${r.company})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {caseData.referrer_name && (
                      <div className="mt-2 space-y-1.5">
                        <p className="text-xs text-violet-600 font-medium">{caseData.referrer_name}{caseData.referrer_company ? ` · ${caseData.referrer_company}` : ""}</p>
                        {caseData.referrer_id && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <ReferralFeeEditor
                              caseId={caseId}
                              value={caseData.referral_fee}
                              disabled={!caseData.referrer_id}
                              onSaved={(updated) => setCaseData(updated)}
                            />
                            {caseData.referrer_fee_paid ? (
                              <button onClick={() => API.put(`/cases/${caseId}/referrer-fee-paid`, { paid: false }).then(({ data }) => { setCaseData(data); toast.success("Marked as unpaid"); }).catch(() => toast.error("Failed"))}
                                data-testid="unmark-paid-btn"
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors">
                                <Check weight="bold" className="w-3 h-3" /> Paid {caseData.referrer_fee_paid_at ? new Date(caseData.referrer_fee_paid_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}
                              </button>
                            ) : (
                              <button onClick={() => API.put(`/cases/${caseId}/referrer-fee-paid`, { paid: true }).then(({ data }) => { setCaseData(data); toast.success("Marked as paid"); }).catch(() => toast.error("Failed"))}
                                data-testid="mark-paid-btn"
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700 transition-colors">
                                Mark Fee as Paid
                              </button>
                            )}
                          </div>
                        )}
                        {/* Read-only payment details surfaced from referrer profile */}
                        {(referrers.find(r => r.id === caseData.referrer_id)?.bank_name ||
                          referrers.find(r => r.id === caseData.referrer_id)?.account_number) && (
                          <div className="mt-2 p-2.5 bg-slate-50 border border-slate-200 rounded-md text-[11px] space-y-0.5" data-testid="referrer-bank-details">
                            {(() => {
                              const r = referrers.find(x => x.id === caseData.referrer_id) || {};
                              return (
                                <>
                                  {r.bank_name && <div><span className="text-slate-500">Bank:</span> <span className="font-medium text-slate-700">{r.bank_name}</span></div>}
                                  {r.account_name && <div><span className="text-slate-500">Account:</span> <span className="font-medium text-slate-700">{r.account_name}</span></div>}
                                  {r.sort_code && <div><span className="text-slate-500">Sort code:</span> <span className="font-mono text-slate-700">{r.sort_code}</span></div>}
                                  {r.account_number && <div><span className="text-slate-500">Account no:</span> <span className="font-mono text-slate-700">{r.account_number}</span></div>}
                                  {r.vat_number && <div><span className="text-slate-500">VAT:</span> <span className="font-mono text-slate-700">{r.vat_number}</span></div>}
                                  {r.payment_notes && <div className="text-slate-600 italic">{r.payment_notes}</div>}
                                </>
                              );
                            })()}
                          </div>
                        )}
                        {/* Auto-generated invoice (visible when case is completed + has referrer fee) */}
                        {caseInvoice && (
                          <div className="mt-2 p-2.5 bg-violet-50 border border-violet-200 rounded-md text-[11px]" data-testid="case-invoice-block">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <Receipt weight="fill" className="w-3.5 h-3.5 text-violet-600 shrink-0" />
                                <span className="font-mono font-semibold text-violet-900 truncate" data-testid="invoice-number">{caseInvoice.invoice_number}</span>
                              </div>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${caseInvoice.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                {caseInvoice.status === "paid" ? "Paid" : "Issued"}
                              </span>
                            </div>
                            <p className="text-slate-600 mb-1.5">
                              {new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(caseInvoice.amount_gross)}
                              {caseInvoice.vat_applied && <span className="text-slate-400"> incl. VAT</span>}
                            </p>
                            <button
                              onClick={async () => {
                                try {
                                  const resp = await API.get(`/invoices/${caseInvoice.id}/pdf`, { responseType: "blob" });
                                  const url = URL.createObjectURL(resp.data);
                                  const a = document.createElement("a");
                                  a.href = url; a.download = `${caseInvoice.invoice_number}.pdf`;
                                  a.click(); URL.revokeObjectURL(url);
                                } catch { toast.error("Failed to download invoice"); }
                              }}
                              data-testid="download-invoice-btn"
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium border border-violet-200 bg-white rounded hover:bg-violet-50 transition-colors">
                              <DownloadSimple weight="bold" className="w-3 h-3" /> Download PDF
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Lead Info — admin-only, shows when case was opened from a contact-only lead */}
                {leadInfo && !leadInfo.promoted && user?.is_admin && (
                  <div className="bg-amber-50/40 border border-amber-200 rounded-lg p-5 shadow-sm" data-testid="lead-info-card">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="text-sm font-semibold text-amber-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <AddressBook weight="bold" className="w-4 h-4" /> Linked Lead
                      </h4>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">No login yet</span>
                    </div>
                    <p className="text-[11px] text-amber-800/80 mb-3">This case was opened from a contact-only lead. Promote them when you're ready to give them a client login.</p>
                    <div className="space-y-1 mb-3 text-xs text-slate-700">
                      <div><span className="text-slate-500">Name:</span> <span className="font-medium">{leadInfo.name}</span></div>
                      {leadInfo.email && <div><span className="text-slate-500">Email:</span> <span className="font-medium">{leadInfo.email}</span></div>}
                      {leadInfo.phone && <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{leadInfo.phone}</span></div>}
                      {leadInfo.notes && <div className="text-slate-600 italic mt-1">"{leadInfo.notes}"</div>}
                    </div>
                    <Button size="sm" onClick={() => setPromoteLeadOpen(true)} disabled={!leadInfo.email}
                      data-testid="case-promote-lead-btn"
                      title={!leadInfo.email ? "Add an email to the lead before promoting" : "Convert to a real client account"}
                      className="h-8 text-xs gap-1.5 bg-amber-600 hover:bg-amber-700 text-white">
                      <Sparkle weight="bold" className="w-3.5 h-3.5" /> Promote to client account
                    </Button>
                  </div>
                )}

                {/* Clients Section */}
                <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="clients-section">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <User weight="bold" className="w-4 h-4 text-slate-500" /> Clients ({caseData.clients?.length || 0})
                  </h4>
                  {(caseData.clients?.length > 0) ? (
                    <div className="space-y-2 mb-3">
                      {caseData.clients.map((c) => {
                        const roleColor = ROLE_COLORS[c.case_role] || ROLE_COLORS.other;
                        const roleLabel = CLIENT_ROLES.find(r => r.value === c.case_role)?.label || c.case_role || "Buyer";
                        const rowKey = c.id || `pending:${c.email}`;
                        const isExpanded = expandedClientKey === rowKey;
                        // Per-case extended fields surfaced inline below the row
                        // when expanded. Empty fields render as "—" so absences
                        // are still glance-able. Mirrors the order used in
                        // ClientDetailsDialog so the two stay coherent.
                        const DETAIL_FIELDS = [
                          { key: "company_name", label: "Company name", showIf: (cl) => (cl.entity_type || "individual") === "company" },
                          { key: "company_registration_number", label: "Company reg. no.", showIf: (cl) => (cl.entity_type || "individual") === "company" },
                          { key: "phone", label: "Phone" },
                          {
                            key: "address",
                            label: "Address",
                            // Structured-first rendering (Feb 2026). When
                            // the solicitor has entered any of the split
                            // fields we compose them into an address block
                            // on separate lines. Falls back to the
                            // freeform ``address`` string only when NONE
                            // of the structured fields are populated —
                            // that way legacy records still render.
                            hasValue: (cl) =>
                              cl.address_line1 || cl.address_line2 || cl.town ||
                              cl.county || cl.postcode || cl.address,
                            render: (cl) => {
                              const lines = [
                                cl.address_line1,
                                cl.address_line2,
                                cl.town,
                                cl.county,
                                cl.postcode,
                              ].map((s) => (s || "").trim()).filter(Boolean);
                              if (lines.length === 0) {
                                // No structured fields — fall back to
                                // the freeform, splitting on comma/newline
                                // so a legacy "12 High St, London, SW1A 1AA"
                                // still renders on multiple lines.
                                const raw = (cl.address || "").trim();
                                if (!raw) return "—";
                                const parts = raw
                                  .split(/\n|,/)
                                  .map((s) => s.trim())
                                  .filter(Boolean);
                                return (
                                  <span className="whitespace-pre-line">{parts.join("\n")}</span>
                                );
                              }
                              return (
                                <span className="whitespace-pre-line">
                                  {lines.join("\n")}
                                </span>
                              );
                            },
                          },
                          { key: "dob", label: "Date of birth" },
                          { key: "ni_number", label: "NI number" },
                          { key: "marital_status", label: "Marital status" },
                          { key: "id_type", label: "ID type" },
                          { key: "id_number", label: "ID number" },
                          { key: "id_expiry", label: "ID expiry" },
                          { key: "aml_status", label: "AML status" },
                          { key: "source_of_funds", label: "Source of funds" },
                          { key: "mortgage_status", label: "Mortgage status" },
                          { key: "notes", label: "Notes" },
                        ];
                        const amlPill = {
                          "Cleared": "bg-emerald-50 text-emerald-700",
                          "Failed": "bg-rose-50 text-rose-700",
                          "In progress": "bg-amber-50 text-amber-700",
                          "Not started": "bg-slate-100 text-slate-600",
                        };
                        return (
                        <div key={rowKey} className="p-2.5 bg-slate-50 border border-slate-100 rounded-lg" data-testid={`client-row-${rowKey}`}>
                          <div className="flex items-center justify-between mb-1">
                            <button
                              type="button"
                              onClick={() => setExpandedClientKey(isExpanded ? null : rowKey)}
                              data-testid={`client-expand-${rowKey}`}
                              title={isExpanded ? "Hide details" : "Show details"}
                              className="flex-1 min-w-0 text-left hover:bg-slate-100/50 rounded -m-0.5 p-0.5 transition-colors"
                            >
                              <div className="flex items-center gap-1.5">
                                <CaretDown weight="bold" className={`w-3 h-3 text-slate-400 shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-slate-900 truncate">{c.name}</p>
                                  {(c.entity_type === "company") && (c.company_name || c.company_registration_number) && (
                                    <p
                                      className="text-[10px] text-violet-700 truncate inline-flex items-center gap-1 mt-0.5"
                                      data-testid={`client-company-info-${rowKey}`}
                                      title="Transacting as a company"
                                    >
                                      <Briefcase weight="fill" className="w-2.5 h-2.5 shrink-0" />
                                      {c.company_name || "Company"}
                                      {c.company_registration_number && (
                                        <span className="text-violet-600/80">· Reg {c.company_registration_number}</span>
                                      )}
                                    </p>
                                  )}
                                  {/* Companies House daily watchdog alert — only shown when a
                                      corporate client has moved into a concerning status
                                      (dissolved / in administration / liquidation etc). */}
                                  {(c.entity_type === "company") && c.company_status_alert?.status && (
                                    <p
                                      className="text-[10px] text-rose-700 truncate inline-flex items-center gap-1 mt-0.5 font-medium"
                                      data-testid={`client-company-alert-${rowKey}`}
                                      title={`Detected by daily Companies House sweep at ${c.company_status_alert.raised_at?.slice(0, 19)?.replace("T", " ")}`}
                                    >
                                      <Warning weight="fill" className="w-2.5 h-2.5 shrink-0" />
                                      Company status: {String(c.company_status_alert.status).replace(/-/g, " ").replace(/\b\w/g, (s) => s.toUpperCase())}
                                    </p>
                                  )}
                                  <p className="text-[10px] text-slate-400 truncate">{c.email}</p>
                                  {c.pending_inheritance?.candidate_fields && Object.keys(c.pending_inheritance.candidate_fields).length > 0 && isSolicitor && (
                                    <p
                                      className="text-[10px] text-amber-800 truncate inline-flex items-center gap-1 mt-0.5 font-medium"
                                      data-testid={`client-pending-inheritance-${rowKey}`}
                                      title={`This client appeared on a prior case (${c.pending_inheritance.source_case_title || "untitled"}). Review before any PII copies across.`}
                                    >
                                      <Info weight="fill" className="w-2.5 h-2.5 shrink-0" />
                                      Review PII from "{c.pending_inheritance.source_case_title || "prior case"}"
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(ev) => {
                                          ev.stopPropagation();
                                          setInheritanceReview({ key: rowKey, entry: c });
                                        }}
                                        onKeyDown={(ev) => {
                                          if (ev.key === "Enter" || ev.key === " ") {
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                            setInheritanceReview({ key: rowKey, entry: c });
                                          }
                                        }}
                                        className="ml-1 rounded bg-amber-200/70 hover:bg-amber-300 text-amber-900 px-1.5 py-0.5 text-[10px] font-semibold transition-colors cursor-pointer"
                                        data-testid={`client-review-inheritance-btn-${rowKey}`}
                                      >
                                        Review
                                      </span>
                                    </p>
                                  )}
                                  {c.inherited_from_case_title && (
                                    <p
                                      className="text-[10px] text-amber-700 truncate inline-flex items-center gap-1 mt-0.5"
                                      data-testid={`client-inherited-note-${rowKey}`}
                                      title={`Some details were auto-filled from a previous case for this client. Edit any field to clear this note, or click × to dismiss.`}
                                    >
                                      <Info weight="fill" className="w-2.5 h-2.5 shrink-0" />
                                      Pulled from "{c.inherited_from_case_title}"
                                      {c.inherited_at && (
                                        <span className="text-amber-600/70">
                                          · {new Date(c.inherited_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                        </span>
                                      )}
                                      {isSolicitor && (
                                        <>
                                          <button
                                            type="button"
                                            onClick={async (ev) => {
                                              ev.stopPropagation();
                                              if (!window.confirm("Clear the auto-filled DOB, NI, ID and address fields for this client? Their name, email and phone stay put — only the copied person-level details are cleared.")) return;
                                              try {
                                                const { data } = await API.post(`/cases/${caseId}/clients/${rowKey}/clear-inherited`);
                                                setCaseData(data.case);
                                                toast.success("Auto-filled fields cleared");
                                              } catch (err) {
                                                toast.error(err.response?.data?.detail || "Failed to clear");
                                              }
                                            }}
                                            className="hover:text-rose-800 transition-colors underline decoration-dotted underline-offset-2"
                                            data-testid={`client-inherited-clear-${rowKey}`}
                                            title="Clear the auto-filled details (DOB, NI, ID, address) — keeps name and email"
                                          >
                                            Clear
                                          </button>
                                          <button
                                            type="button"
                                            onClick={async (ev) => {
                                              ev.stopPropagation();
                                              try {
                                                const { data } = await API.post(`/cases/${caseId}/clients/${rowKey}/dismiss-inherited`);
                                                setCaseData(data);
                                              } catch (err) {
                                                toast.error(err.response?.data?.detail || "Failed to dismiss");
                                              }
                                            }}
                                            className="hover:text-amber-900 transition-colors"
                                            data-testid={`client-inherited-dismiss-${rowKey}`}
                                            title="Dismiss this note (keeps the copied fields)"
                                          >
                                            <X weight="bold" className="w-2.5 h-2.5" />
                                          </button>
                                        </>
                                      )}
                                    </p>
                                  )}
                                  {/* Last invite send status — only renders
                                      once the invite has been sent at least
                                      once. Green = Microsoft Graph accepted
                                      it; slate = mocked (preview); rose =
                                      send failed. Click target opens the
                                      raw error in a toast if any. */}
                                  {c.last_invite_sent_at && (
                                    <p
                                      className={`text-[10px] truncate inline-flex items-center gap-1 mt-0.5 ${
                                        c.last_invite_status === "outlook" ? "text-emerald-700" :
                                        c.last_invite_status === "error" ? "text-rose-700" :
                                        c.last_invite_status === "blocked" ? "text-amber-700" :
                                        "text-slate-500"
                                      }`}
                                      data-testid={`client-invite-status-${rowKey}`}
                                      title={
                                        c.last_invite_status === "outlook"
                                          ? `Sent via Outlook${c.last_invite_message_id ? ` · message-id: ${c.last_invite_message_id}` : ""}`
                                          : c.last_invite_status === "error"
                                            ? `Send failed: ${c.last_invite_error || "Unknown error"}`
                                            : c.last_invite_status === "blocked"
                                              ? "Preview safety filter is on — the invite was not delivered to a real inbox. In production the client will receive this email normally. Ask an admin to add this address to OUTBOUND_EMAIL_ALLOWLIST if you need to test delivery on preview."
                                              : `Mock send — no email provider configured`
                                      }
                                    >
                                      {c.last_invite_status === "outlook"
                                        ? <CheckCircle weight="fill" className="w-2.5 h-2.5 shrink-0" />
                                        : c.last_invite_status === "error"
                                          ? <Warning weight="fill" className="w-2.5 h-2.5 shrink-0" />
                                          : c.last_invite_status === "blocked"
                                            ? <Warning weight="regular" className="w-2.5 h-2.5 shrink-0" />
                                            : <CircleDashed weight="bold" className="w-2.5 h-2.5 shrink-0" />}
                                      {c.last_invite_status === "outlook"
                                        ? `Invite sent ✓ · ${new Date(c.last_invite_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                                        : c.last_invite_status === "error"
                                          ? `Send failed · ${(c.last_invite_error || "").slice(0, 36)}`
                                          : c.last_invite_status === "blocked"
                                            ? `Held by preview safety filter · would send in production`
                                            : `Mocked · ${new Date(c.last_invite_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
                                      {(c.invite_sent_count || 0) > 1 && (
                                        <span className="text-slate-400">· {c.invite_sent_count}×</span>
                                      )}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </button>
                            {isSolicitor && (
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={async () => {
                                    try {
                                      const { data } = await API.post(`/cases/${caseId}/clients/${rowKey}/inherit`);
                                      const filled = data._inherited_fields || [];
                                      if (filled.length) {
                                        toast.success(`Pulled ${filled.length} field${filled.length === 1 ? "" : "s"} from "${data._prior_case_title}"`);
                                        setCaseData(data);
                                      } else {
                                        toast.info("Nothing to pull — already filled or no matching prior case");
                                      }
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || "Pull failed");
                                    }
                                  }}
                                  data-testid={`inherit-client-${rowKey}`}
                                  title="Pull missing details from this client's prior case"
                                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-amber-100 text-slate-400 hover:text-amber-700 transition-colors"
                                >
                                  <ArrowsClockwise weight="bold" className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleOrderAmlCheck(c)}
                                  disabled={!c.email}
                                  data-testid={`order-aml-${rowKey}`}
                                  title={c.aml_bucket && c.aml_bucket !== "none" ? "Re-run AML check" : "Order AML / ID-V check"}
                                  className={`w-6 h-6 flex items-center justify-center rounded transition-colors ${
                                    c.aml_bucket === "clear" ? "text-emerald-600 hover:bg-emerald-50" :
                                    c.aml_bucket === "fail" ? "text-rose-600 hover:bg-rose-50" :
                                    c.aml_bucket === "warn" ? "text-amber-600 hover:bg-amber-50" :
                                    "text-slate-400 hover:bg-slate-100 hover:text-[#1462a0]"
                                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                                >
                                  <ShieldCheck weight={c.aml_bucket && c.aml_bucket !== "open" && c.aml_bucket !== "none" ? "fill" : "bold"} className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => setEditingClient(c)}
                                  data-testid={`edit-client-${rowKey}`}
                                  title="View / edit client details"
                                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                                >
                                  <PencilSimple weight="bold" className="w-3 h-3" />
                                </button>
                                <button onClick={() => handleRemoveClient(c)} data-testid={`remove-client-${rowKey}`}
                                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors">
                                  <Trash weight="bold" className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                            {!isSolicitor && (
                              <button
                                onClick={() => setEditingClient(c)}
                                data-testid={`view-client-${rowKey}`}
                                title="View client details"
                                className="shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                              >
                                <PencilSimple weight="regular" className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {isSolicitor ? (
                              <Select value={c.case_role || "buyer"} onValueChange={(v) => handleUpdateClientRole(c, v)}>
                                <SelectTrigger className={`h-5 px-2 text-[10px] font-medium border-0 rounded-full w-auto gap-0.5 ${roleColor}`} data-testid={`client-role-select-${rowKey}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CLIENT_ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full ${roleColor}`} data-testid={`client-role-badge-${rowKey}`}>
                                {roleLabel}
                              </span>
                            )}
                            {c.aml_bucket && c.aml_bucket !== "none" && (
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                  c.aml_bucket === "clear" ? "bg-emerald-50 text-emerald-700" :
                                  c.aml_bucket === "fail" ? "bg-rose-50 text-rose-700" :
                                  c.aml_bucket === "warn" ? "bg-amber-50 text-amber-700" :
                                  "bg-blue-50 text-blue-700"
                                }`}
                                data-testid={`client-aml-badge-${rowKey}`}
                                title={`AML status: ${c.aml_verdict || c.aml_status || c.aml_bucket}${c.aml_ordered_at ? ` · ordered ${new Date(c.aml_ordered_at).toLocaleDateString("en-GB")}` : ""}`}
                              >
                                <ShieldCheck weight={c.aml_bucket === "clear" ? "fill" : "bold"} className="w-2.5 h-2.5" />
                                AML: {c.aml_bucket === "clear" ? "Clear" : c.aml_bucket === "warn" ? "Refer" : c.aml_bucket === "fail" ? "Failed" : "Pending"}
                              </span>
                            )}
                            {c.pending_account && (
                              <>
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700"
                                  title="Will auto-link when this client creates their portal account"
                                  data-testid={`client-pending-badge-${rowKey}`}
                                >
                                  <Clock weight="bold" className="w-2.5 h-2.5" /> Pending account
                                </span>
                                {isSolicitor && (
                                  <button
                                    onClick={() => handleResendInvite(c)}
                                    data-testid={`resend-invite-${rowKey}`}
                                    title="Re-send the sign-up invite email"
                                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  >
                                    <PaperPlaneTilt weight="bold" className="w-2.5 h-2.5" />
                                    Resend invite
                                  </button>
                                )}
                              </>
                            )}
                            {isSolicitor && c.email && !c.pending_account && (
                              <button
                                onClick={() => handleResendInvite(c)}
                                data-testid={`resend-signin-${rowKey}`}
                                title="Re-send this client's secure sign-in link"
                                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                              >
                                <PaperPlaneTilt weight="bold" className="w-2.5 h-2.5" />
                                Resend sign-in link
                              </button>
                            )}
                          </div>
                          {isExpanded && (
                            <dl className="mt-2 pt-2 border-t border-slate-200 space-y-1.5" data-testid={`client-details-inline-${rowKey}`}>
                              {DETAIL_FIELDS.map((f) => {
                                if (f.showIf && !f.showIf(c)) return null;
                                // Custom hasValue lets structured fields
                                // (e.g. Address, composed from 5 pieces)
                                // decide their own presence check.
                                const present = f.hasValue ? f.hasValue(c) : c[f.key];
                                if (!present && f.key !== "aml_status") return null;
                                return (
                                  <div key={f.key} className="grid grid-cols-3 gap-2 text-[11px]">
                                    <dt className="text-slate-500 col-span-1">{f.label}</dt>
                                    <dd className="text-slate-900 col-span-2 break-words">
                                      {f.key === "aml_status" ? (
                                        <span className={`inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded-full ${amlPill[c[f.key]] || "bg-slate-100 text-slate-500"}`}>
                                          {c[f.key] || "Not started"}
                                        </span>
                                      ) : f.render ? f.render(c) : (c[f.key] || "—")}
                                    </dd>
                                  </div>
                                );
                              })}
                              {!DETAIL_FIELDS.some(f => (f.hasValue ? f.hasValue(c) : c[f.key])) && (
                                <p className="text-[11px] text-slate-400 italic">
                                  No extended details captured yet{isSolicitor ? " — click the pencil to add." : "."}
                                </p>
                              )}
                            </dl>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 mb-3">No clients assigned</p>
                  )}
                  {isSolicitor && (
                    <div className="space-y-2" data-testid="add-client-form">
                      <Input
                        data-testid="add-client-name-input"
                        placeholder="Full name"
                        value={addClientName}
                        onChange={(e) => setAddClientName(e.target.value)}
                        className="h-8 text-xs"
                      />
                      <Input
                        data-testid="add-client-email-input"
                        type="email"
                        placeholder="client@email.com"
                        value={addClientEmail}
                        onChange={(e) => setAddClientEmail(e.target.value)}
                        className="h-8 text-xs"
                        onKeyDown={(e) => e.key === "Enter" && handleAddClient()}
                      />
                      <Input
                        data-testid="add-client-phone-input"
                        placeholder="Phone (optional)"
                        value={addClientPhone}
                        onChange={(e) => setAddClientPhone(e.target.value)}
                        className="h-8 text-xs"
                      />
                      <div className="flex items-center gap-1.5">
                        <Select value={addClientRole} onValueChange={setAddClientRole}>
                          <SelectTrigger className="h-8 text-xs flex-1" data-testid="add-client-role-select">
                            <SelectValue placeholder="Role" />
                          </SelectTrigger>
                          <SelectContent>
                            {CLIENT_ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={handleAddClient}
                          disabled={addingClient || !addClientEmail.trim() || !addClientName.trim()}
                          data-testid="add-client-btn"
                          className="h-8 px-3 text-xs bg-slate-900 hover:bg-slate-800 text-white"
                        >
                          {addingClient ? "..." : "Add"}
                        </Button>
                      </div>
                      <p className="text-[10px] text-slate-400 leading-relaxed">
                        No portal account needed — the client auto-links when they sign up later.
                      </p>
                    </div>
                  )}
                </div>
                {/* Share Link */}
                {isSolicitor && (
                  <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="share-link-section">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <LinkSimple weight="bold" className="w-4 h-4 text-slate-500" /> Agent Link
                    </h4>
                    {shareToken ? (
                      <div className="space-y-2.5">
                        <div className="flex items-center gap-1.5 p-2 bg-slate-50 border border-slate-200 rounded-lg">
                          <input
                            readOnly
                            value={`${window.location.origin}/portal/${shareToken}`}
                            className="flex-1 text-xs text-slate-600 bg-transparent outline-none truncate"
                            data-testid="share-link-input"
                          />
                          <button
                            onClick={handleCopyShareLink}
                            data-testid="copy-share-link-btn"
                            className="shrink-0 w-7 h-7 flex items-center justify-center rounded hover:bg-slate-200 transition-colors"
                          >
                            <Copy weight="bold" className="w-3.5 h-3.5 text-slate-600" />
                          </button>
                        </div>
                        <button
                          onClick={handleRevokeShareLink}
                          data-testid="revoke-share-link-btn"
                          className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
                        >
                          <Trash weight="bold" className="w-3 h-3" /> Revoke link
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs text-slate-500 mb-2.5">Share a read-only link so agents can track progress without an account.</p>
                        <Button
                          onClick={handleGenerateShareLink}
                          disabled={generatingLink}
                          data-testid="generate-share-link-btn"
                          variant="outline"
                          className="w-full h-8 text-xs border-slate-200"
                        >
                          <LinkSimple weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                          {generatingLink ? "Generating..." : "Generate Share Link"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {/* Composer for ad-hoc updates that land on the Agent
                    Link page. The feed itself is deliberately not shown
                    here — solicitors see it only on the agent page. */}
                {isSolicitor && shareToken && (
                  <AgentNotesPanel
                    caseId={caseId}
                    onCaseUpdated={(updated) => setCaseData(updated)}
                  />
                )}
              </div>
            </div>
            {/* Internal solicitor-only file notes — sits directly under the
                Conveyancing Timeline section. Hidden from clients. */}
            <ReportDetailsPanel
              caseData={caseData}
              isSolicitor={isSolicitor}
              onUpdated={(updated) => setCaseData(updated)}
            />
            <FileNotesPanel
              caseId={caseId}
              isSolicitor={isSolicitor}
              stages={caseData.stages || []}
              documents={documents}
            />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Documents</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {isSolicitor && (
                    <GenerateFromTemplateButton
                      caseId={caseId}
                      caseTitle={caseData.title}
                      clients={caseData.clients}
                      referrer={caseData.referrer_id ? { id: caseData.referrer_id, name: caseData.referrer_name, email: caseData.referrer_email || "set" } : null}
                      onGenerated={() => {
                        // Refresh docs after merge so the new files surface in
                        // the active folder pane (WS broadcast already covers
                        // remote clients, this is the local belt-and-braces).
                        API.get(`/cases/${caseId}/documents`).then(({ data }) => setDocuments(data)).catch(() => {});
                      }}
                    />
                  )}
                  <Button
                    onClick={handleDownloadReport}
                    variant="outline"
                    data-testid="download-report-btn"
                    className="h-9 px-3 text-sm border-slate-200 gap-1.5"
                  >
                    <FilePdf weight="bold" className="w-4 h-4 text-red-500" /> PDF Report
                  </Button>
                </div>
              </div>

              {/* PDF text search — full-text search across every PDF
                  attached to this case. Index is built lazily server-side
                  on the first query (and refreshed on each upload). */}
              <div className="mb-4" data-testid="pdf-search-block">
                <form onSubmit={runPdfSearch} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <MagnifyingGlass weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={pdfSearchQuery}
                      onChange={(e) => setPdfSearchQuery(e.target.value)}
                      placeholder="Search inside PDFs on this case (e.g. easement, completion date, £)…"
                      className="pl-8 h-9 text-sm"
                      data-testid="pdf-search-input"
                    />
                    {pdfSearchQuery && (
                      <button
                        type="button"
                        onClick={() => { setPdfSearchQuery(""); setPdfSearchResults(null); setPdfSearchError(null); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded hover:bg-slate-100"
                        data-testid="pdf-search-clear"
                        aria-label="Clear search"
                      >
                        <X weight="bold" className="w-3 h-3 text-slate-500" />
                      </button>
                    )}
                  </div>
                  <Button
                    type="submit"
                    disabled={pdfSearchLoading || !pdfSearchQuery.trim()}
                    className="h-9 px-3 text-sm gap-1.5"
                    data-testid="pdf-search-submit"
                  >
                    {pdfSearchLoading ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Searching…
                      </>
                    ) : (
                      <>Search PDFs</>
                    )}
                  </Button>
                </form>

                {pdfSearchError && (
                  <p className="text-xs text-rose-600 mt-2" data-testid="pdf-search-error">{pdfSearchError}</p>
                )}

                {pdfSearchResults != null && !pdfSearchLoading && (
                  <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden bg-slate-50" data-testid="pdf-search-results">
                    {pdfSearchResults.length === 0 ? (
                      <p className="text-xs text-slate-500 px-3 py-3" data-testid="pdf-search-empty">
                        No matches found for "{pdfSearchQuery.trim()}". Scanned PDFs without OCR can't be searched yet.
                      </p>
                    ) : (
                      <>
                        <p className="text-[11px] text-slate-500 px-3 py-1.5 bg-slate-100 border-b border-slate-200">
                          {pdfSearchResults.length} match{pdfSearchResults.length === 1 ? "" : "es"} across {new Set(pdfSearchResults.map((r) => r.doc_id)).size} document{new Set(pdfSearchResults.map((r) => r.doc_id)).size === 1 ? "" : "s"}
                        </p>
                        <ul className="divide-y divide-slate-200 max-h-72 overflow-auto">
                          {pdfSearchResults.map((hit, idx) => (
                            <li key={`${hit.doc_id}-${hit.page}-${idx}`}>
                              <button
                                type="button"
                                onClick={() => openSearchHit(hit)}
                                className="w-full text-left px-3 py-2.5 hover:bg-white transition-colors group"
                                data-testid={`pdf-search-hit-${idx}`}
                              >
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="text-xs font-medium text-slate-900 truncate group-hover:text-blue-600 flex items-center gap-1.5">
                                    <FileText weight="fill" className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                    {hit.filename}
                                  </span>
                                  <span className="text-[10px] font-medium text-slate-500 tabular-nums px-1.5 py-0.5 rounded bg-slate-200/70 shrink-0">
                                    p.{hit.page}
                                  </span>
                                </div>
                                {(hit.snippets || []).map((s, i) => (
                                  <p
                                    key={i}
                                    className="text-[11px] text-slate-600 leading-snug pl-5"
                                    // Backend escapes user content; only **…**
                                    // markers come from snippet builder so we
                                    // can render them as <mark> safely.
                                    dangerouslySetInnerHTML={{
                                      __html: s
                                        .replace(/</g, "&lt;")
                                        .replace(/>/g, "&gt;")
                                        .replace(/\*\*(.+?)\*\*/g, '<mark class="bg-yellow-200 text-slate-900 rounded px-0.5">$1</mark>'),
                                    }}
                                  />
                                ))}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Drag & Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                data-testid="drop-zone"
                className={`relative mb-4 border-2 border-dashed rounded-xl transition-all duration-200 ${
                  isDragging
                    ? "border-blue-400 bg-blue-50/60 scale-[1.01]"
                    : "border-slate-200 bg-slate-50/50 hover:border-slate-300"
                }`}
              >
                <label className="flex flex-col items-center justify-center py-6 cursor-pointer">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-2.5 transition-colors ${isDragging ? "bg-blue-100" : "bg-slate-100"}`}>
                    <CloudArrowUp weight="duotone" className={`w-5 h-5 transition-colors ${isDragging ? "text-blue-600" : "text-slate-400"}`} />
                  </div>
                  {uploading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                      <p className="text-sm font-medium text-blue-600">Uploading...</p>
                    </div>
                  ) : isDragging ? (
                    <p className="text-sm font-medium text-blue-600">Drop files here</p>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Drag & drop files here, or <span className="text-blue-600 underline underline-offset-2">browse</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Upload multiple files at once</p>
                    </>
                  )}
                  <input type="file" multiple className="hidden" onChange={handleUploadDoc} disabled={uploading} data-testid="upload-doc-input" />
                </label>
              </div>

              {/* Folder navigation — preset 8 + custom + "All" / "Unfiled".
                  The active folder filters the doc list and also targets new
                  uploads (so dropping into "Searches" puts the file there).
                  Solicitors can rename / toggle client-visibility / delete
                  custom folders inline; presets show only the rename action. */}
              <div className="mb-4 -mx-1" data-testid="folder-bar">
                <div className="flex flex-wrap items-center gap-2 pb-2 px-1">
                  {[
                    { id: "all", name: `All (${documents.length})`, isAll: true },
                    { id: "uncategorised", name: `Unfiled (${documents.filter((d) => !d.folder_id).length})`, isAll: true },
                    ...folders.map((f) => ({
                      ...f,
                      count: documents.filter((d) => d.folder_id === f.id).length,
                    })),
                  ].map((f) => {
                    const active = activeFolderId === f.id;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setActiveFolderId(f.id)}
                        data-testid={`folder-tab-${f.id}`}
                        className={`shrink-0 inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-xs font-medium transition-colors ${
                          active
                            ? "bg-slate-900 border-slate-900 text-white"
                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {f.isAll ? (
                          <FolderOpen weight={active ? "fill" : "regular"} className="w-3.5 h-3.5" />
                        ) : (
                          <Folder weight={active ? "fill" : "regular"} className="w-3.5 h-3.5" />
                        )}
                        <span>{f.name}</span>
                        {!f.isAll && (
                          <>
                            {f.hidden_from_client && (
                              <EyeSlash weight="bold" className={`w-3 h-3 ${active ? "text-amber-200" : "text-amber-500"}`} title="Hidden from client portal" />
                            )}
                            <span className={`text-[10px] ${active ? "text-slate-300" : "text-slate-400"}`}>{f.count}</span>
                          </>
                        )}
                      </button>
                    );
                  })}
                  {isSolicitor && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFolderDialogOpen(true)}
                      data-testid="add-folder-btn"
                      className="shrink-0 h-8 text-xs gap-1 border-dashed"
                    >
                      <Plus weight="bold" className="w-3.5 h-3.5" /> New folder
                    </Button>
                  )}
                </div>
                {/* Inline actions for the currently active custom folder */}
                {isSolicitor && activeFolderId !== "all" && activeFolderId !== "uncategorised" && (() => {
                  const f = folders.find((x) => x.id === activeFolderId);
                  if (!f) return null;
                  return (
                    <div className="flex items-center gap-2 px-1 pt-1 text-xs text-slate-500" data-testid={`folder-actions-${f.id}`}>
                      <button type="button" onClick={() => handleRenameFolder(f.id, f.name)} className="hover:text-slate-900 inline-flex items-center gap-1" data-testid={`rename-folder-${f.id}`}>
                        <PencilSimple weight="bold" className="w-3 h-3" /> Rename
                      </button>
                      <span className="text-slate-300">·</span>
                      <button type="button" onClick={() => handleToggleFolderHidden(f.id, f.hidden_from_client)} className="hover:text-slate-900 inline-flex items-center gap-1" data-testid={`toggle-hidden-folder-${f.id}`}>
                        <EyeSlash weight={f.hidden_from_client ? "fill" : "regular"} className={`w-3 h-3 ${f.hidden_from_client ? "text-amber-600" : ""}`} />
                        {f.hidden_from_client ? "Visible to client" : "Hide from client"}
                      </button>
                      {!f.is_preset && (
                        <>
                          <span className="text-slate-300">·</span>
                          <button type="button" onClick={() => handleDeleteFolder(f.id, f.name)} className="hover:text-red-600 inline-flex items-center gap-1" data-testid={`delete-folder-${f.id}`}>
                            <Trash weight="bold" className="w-3 h-3" /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Native e-signing — signing requests live in the Documents
                  folder. Launch one via "Send for signing" on any PDF row. */}
              {isSolicitor && (
                <ESignPanel
                  caseId={caseId}
                  refreshKey={esignRefresh}
                  autoFinaliseEnvelopeId={searchParams.get("finalise")}
                  onEditDraft={(env) => setEsignDesigner(env)}
                  onChanged={() => { setEsignRefresh((x) => x + 1); API.get(`/cases/${caseId}/documents`).then(({ data }) => setDocuments(data)).catch(() => {}); }}
                />
              )}

              {/* Upload category selector */}
              <div className="flex items-center gap-3 mb-5">
                <span className="text-xs text-slate-500 font-medium flex items-center gap-1"><Tag weight="bold" className="w-3 h-3" /> Upload as:</span>
                <Select value={uploadCategory} onValueChange={setUploadCategory}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {documents.length > 0 && (
                  <>
                    <span className="text-slate-300 mx-1">|</span>
                    <span className="text-xs text-slate-500 font-medium">Filter:</span>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {DOC_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>

              {/* Document list */}
              {documents.length === 0 ? (
                <div className="text-center py-6" data-testid="no-documents">
                  <FileText weight="thin" className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No documents uploaded yet</p>
                </div>
              ) : (() => {
                let filtered = filterCategory === "all" ? documents : documents.filter((d) => (d.category || "uncategorised") === filterCategory);
                if (activeFolderId === "uncategorised") {
                  filtered = filtered.filter((d) => !d.folder_id);
                } else if (activeFolderId !== "all") {
                  filtered = filtered.filter((d) => d.folder_id === activeFolderId);
                }
                // Split pinned vs unpinned so pinned docs surface in their
                // own section at the top. Pinned ordered by ``pinned_at``
                // desc (newest pin first) — matches the backend order.
                const pinned = filtered
                  .filter((d) => d.pinned)
                  .sort((a, b) => new Date(b.pinned_at || 0) - new Date(a.pinned_at || 0));
                const rest = filtered.filter((d) => !d.pinned);
                return (
                <div>
                  <p className="text-xs text-slate-500 mb-3 font-medium">{filtered.length} document{filtered.length !== 1 ? "s" : ""}{filterCategory !== "all" ? ` in ${DOC_CATEGORIES.find(c => c.value === filterCategory)?.label}` : ""}{pinned.length > 0 ? ` · ${pinned.length} pinned` : ""}</p>
                  {filtered.length === 0 ? (
                    <p className="text-center text-xs text-slate-400 py-6">No documents in this category</p>
                  ) : (
                  <>
                  {isSolicitor && (
                    <BulkActionBar
                      count={[...selectedDocs].filter((id) => filtered.some((d) => d.id === id)).length}
                      onClear={() => setSelectedDocs(new Set())}
                      onDownload={handleBulkDownloadDocs}
                      onDelete={handleBulkDeleteDocs}
                      onMove={handleBulkMoveDocs}
                      onCopy={handleBulkCopyDocs}
                      label="document"
                      variant="active"
                      testIdPrefix="docs"
                    />
                  )}
                  <div className="space-y-2">
                    {(() => {
                      const renderDocRow = (doc) => {
                      const cat = doc.category || "uncategorised";
                      const catLabel = DOC_CATEGORIES.find(c => c.value === cat)?.label || "Uncategorised";
                      const catColor = CATEGORY_COLORS[cat] || CATEGORY_COLORS.uncategorised;
                      const expiry = isSolicitor ? getExpiryStatus(doc.expiry_date) : null;
                      const isSel = selectedDocs.has(doc.id);
                      return (
                      <div key={doc.id} className={`flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors ${isSel ? "border-[#100f5d] ring-2 ring-[#100f5d]/15 bg-[#100f5d]/5" :
                        expiry?.icon === "expired" ? "border-red-200 bg-red-50/30" : expiry?.icon === "warning" ? "border-amber-200 bg-amber-50/20" : "border-slate-200"
                      }`} data-testid={`document-${doc.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          {isSolicitor && (
                            <Checkbox checked={isSel} onCheckedChange={() => toggleDocSel(doc.id)} data-testid={`doc-cb-${doc.id}`} className="shrink-0" />
                          )}
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                            expiry?.icon === "expired" ? "bg-red-100" : "bg-blue-50"
                          }`}>
                            <FileText weight="fill" className={`w-5 h-5 ${expiry?.icon === "expired" ? "text-red-500" : "text-blue-600"}`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <button
                                type="button"
                                onClick={() => setViewerDoc(doc)}
                                className="text-sm font-medium text-slate-900 truncate hover:text-blue-600 hover:underline text-left"
                                data-testid={`view-doc-filename-${doc.id}`}
                                title="Open preview"
                              >
                                {doc.original_filename}
                              </button>
                              <Select value={cat} onValueChange={(v) => handleUpdateDocCategory(doc.id, v)}>
                                <SelectTrigger className={`h-5 px-1.5 text-[10px] font-medium border-0 rounded-full w-auto gap-0.5 ${catColor}`} data-testid={`doc-category-${doc.id}`}>
                                  <Tag weight="fill" className="w-2.5 h-2.5" /><SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {DOC_CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                                </SelectContent>
                              </Select>
                              {expiry && (
                                <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${expiry.color}`} data-testid={`doc-expiry-badge-${doc.id}`}>
                                  {expiry.icon === "expired" && <Warning weight="fill" className="w-2.5 h-2.5" />}
                                  {expiry.icon === "warning" && <Clock weight="fill" className="w-2.5 h-2.5" />}
                                  {expiry.label}
                                </span>
                              )}
                              {docSigningStatus[doc.id] && (
                                <span
                                  data-testid={`doc-signing-badge-${doc.id}`}
                                  title={`Signing status: ${docSigningStatus[doc.id].label}`}
                                  className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${docSigningStatus[doc.id].cls}`}
                                >
                                  <Signature weight="bold" className="w-2.5 h-2.5" /> {docSigningStatus[doc.id].label}
                                </span>
                              )}
                              {isSolicitor && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const { data } = await API.put(`/documents/${doc.id}/share`, { shared_with_client: !doc.shared_with_client });
                                      setDocuments((prev) => prev.map((d) => d.id === doc.id ? { ...d, shared_with_client: data.shared_with_client, shared_at: data.shared_at } : d));
                                      toast.success(data.shared_with_client ? "Shared with client" : "Hidden from client");
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || "Failed to update sharing");
                                    }
                                  }}
                                  data-testid={`doc-share-${doc.id}`}
                                  title={doc.shared_with_client ? "Shared with client — click to hide" : "Not shared — click to share with client"}
                                  className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full transition-colors ${
                                    doc.shared_with_client
                                      ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                  }`}
                                >
                                  {doc.shared_with_client ? "🔓 Client" : "🔒 Private"}
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{doc.uploaded_by_name} · {new Date(doc.created_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => setViewerDoc(doc)}
                            data-testid={`view-doc-${doc.id}`}
                            title="View document"
                            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            <Eye weight="bold" className="w-4 h-4 text-blue-600" />
                          </button>
                          {isSolicitor && (
                            <button
                              type="button"
                              onClick={() => handleTogglePin(doc)}
                              data-testid={`doc-pin-${doc.id}`}
                              title={doc.pinned ? "Unpin from top" : "Pin to top"}
                              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                                doc.pinned ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-slate-100"
                              }`}
                            >
                              {doc.pinned
                                ? <PushPin weight="fill" className="w-4 h-4 text-amber-500" />
                                : <PushPin className="w-4 h-4 text-slate-400" />}
                            </button>
                          )}
                          {isSolicitor && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button data-testid={`doc-expiry-edit-${doc.id}`}
                                  className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                                    doc.expiry_date ? "hover:bg-slate-100" : "hover:bg-slate-100 text-slate-300"
                                  }`}
                                  title="Set expiry date"
                                >
                                  <CalendarBlank weight={doc.expiry_date ? "fill" : "regular"} className={`w-4 h-4 ${
                                    expiry?.icon === "expired" ? "text-red-500" : expiry?.icon === "warning" ? "text-amber-500" : "text-slate-400"
                                  }`} />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="end">
                                <div className="p-2 border-b border-slate-100">
                                  <p className="text-xs font-medium text-slate-700 mb-1">Document Expiry</p>
                                  {doc.expiry_date && (
                                    <button onClick={() => handleUpdateDocExpiry(doc.id, null)}
                                      data-testid={`clear-expiry-${doc.id}`}
                                      className="text-[10px] text-red-500 hover:underline font-medium">
                                      Clear expiry date
                                    </button>
                                  )}
                                </div>
                                <Calendar
                                  mode="single"
                                  selected={doc.expiry_date ? new Date(doc.expiry_date) : undefined}
                                  onSelect={(date) => {
                                    if (date) handleUpdateDocExpiry(doc.id, format(date, "yyyy-MM-dd"));
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          )}
                          {isSolicitor && ((doc.original_filename || "").toLowerCase().endsWith(".docx") || (doc.original_filename || "").toLowerCase().endsWith(".doc")) && (
                            <button
                              onClick={() => handleConvertToPdf(doc)}
                              disabled={convertingDocId === doc.id}
                              data-testid={`convert-pdf-${doc.id}`}
                              title="Convert to PDF"
                              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-50"
                            >
                              {convertingDocId === doc.id
                                ? <span className="w-4 h-4 border-2 border-rose-200 border-t-rose-600 rounded-full animate-spin" />
                                : <FilePdf weight="bold" className="w-4 h-4 text-rose-600" />}
                            </button>
                          )}
                          <button onClick={() => handleDownloadDoc(doc)} data-testid={`download-doc-${doc.id}`}
                            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
                            <DownloadSimple className="w-4 h-4 text-slate-600" />
                          </button>
                          {isSolicitor && ((doc.content_type === "application/pdf") || (doc.original_filename || "").toLowerCase().endsWith(".pdf") || (doc.original_filename || "").toLowerCase().endsWith(".docx")) && (
                            <button
                              onClick={() => setEsignModeDoc(doc)}
                              data-testid={`send-for-signing-${doc.id}`}
                              title="Send for signing"
                              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-indigo-50 transition-colors"
                            >
                              <Signature weight="bold" className="w-4 h-4 text-indigo-600" />
                            </button>
                          )}
                          {isSolicitor && folders.length > 0 && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button data-testid={`doc-move-${doc.id}`}
                                  title="Move to folder"
                                  className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
                                  <Folder className="w-4 h-4 text-slate-500" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-52 p-1" align="end">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-2 py-1.5">Move to</div>
                                <button onClick={() => handleMoveDocToFolder(doc.id, null)}
                                  data-testid={`doc-move-${doc.id}-root`}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-slate-50 ${!doc.folder_id ? "text-slate-900 font-semibold" : "text-slate-600"}`}>
                                  <FolderOpen weight="regular" className="w-3.5 h-3.5" /> Case root (unfiled)
                                </button>
                                {folders.map((f) => (
                                  <button key={f.id} onClick={() => handleMoveDocToFolder(doc.id, f.id)}
                                    data-testid={`doc-move-${doc.id}-${f.id}`}
                                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-slate-50 ${doc.folder_id === f.id ? "text-slate-900 font-semibold bg-slate-100" : "text-slate-600"}`}>
                                    <Folder weight="regular" className="w-3.5 h-3.5" /> <span className="truncate">{f.name}</span>
                                    {f.hidden_from_client && <EyeSlash weight="bold" className="w-3 h-3 ml-auto text-amber-500" />}
                                  </button>
                                ))}
                              </PopoverContent>
                            </Popover>
                          )}
                          {isSolicitor && (
                            <button onClick={() => handleDeleteDocument(doc)} data-testid={`delete-doc-${doc.id}`}
                              title="Delete document"
                              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors">
                              <Trash className="w-4 h-4 text-slate-400 hover:text-red-600" />
                            </button>
                          )}
                        </div>
                      </div>
                      );
                      };
                      return (
                        <>
                          {pinned.length > 0 && (
                            <>
                              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5" data-testid="docs-pinned-header">
                                <PushPin weight="fill" className="w-3 h-3 text-amber-500" />
                                Pinned ({pinned.length})
                              </div>
                              <div className="space-y-2 mb-3" data-testid="docs-pinned-section">
                                {pinned.map(renderDocRow)}
                              </div>
                              {rest.length > 0 && (
                                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 mt-3">
                                  All documents
                                </div>
                              )}
                            </>
                          )}
                          {rest.map(renderDocRow)}
                        </>
                      );
                    })()}
                  </div>
                  </>
                  )}
                </div>
                );
              })()}
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Tasks</h3>
                {isSolicitor && (
                  <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="create-task-btn" className="bg-slate-900 hover:bg-slate-800 text-white h-9 px-4 text-sm">
                        <Plus weight="bold" className="w-4 h-4 mr-1.5" /> Add Task
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create Task</DialogTitle></DialogHeader>
                      <form onSubmit={handleCreateTask} className="space-y-4" data-testid="create-task-form">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Title</Label>
                          <Input data-testid="task-title-input" placeholder="e.g., Review search results" value={newTask.title}
                            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Description</Label>
                          <Textarea data-testid="task-description-input" placeholder="Optional details..." value={newTask.description}
                            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} rows={3} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Assign To</Label>
                          <Select value={newTask.assigned_to} onValueChange={(v) => setNewTask({ ...newTask, assigned_to: v })}>
                            <SelectTrigger data-testid="task-assign-select">
                              <SelectValue placeholder="Select a person" />
                            </SelectTrigger>
                            <SelectContent>
                              {caseMembers.map((m) => (
                                <SelectItem key={m.id} value={m.id}>
                                  <span className="flex items-center gap-2">
                                    <UserCircle weight="fill" className="w-4 h-4 text-slate-400 inline" />
                                    {m.name} <span className="text-xs text-slate-400">({m.role})</span>
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Due Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" data-testid="task-due-date-btn" className="w-full justify-start text-left font-normal">
                                <CalendarBlank className="w-4 h-4 mr-2 text-slate-400" />
                                {calendarDate ? format(calendarDate, "dd MMM yyyy") : "Pick a date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={calendarDate} onSelect={setCalendarDate} initialFocus /></PopoverContent>
                          </Popover>
                        </div>
                        <Button type="submit" data-testid="submit-task-btn" disabled={creatingTask} className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                          {creatingTask ? "Creating..." : "Create Task"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              {tasks.length === 0 ? (
                <div className="text-center py-10" data-testid="no-tasks"><ListChecks weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No tasks yet</p></div>
              ) : (
                <>
                {isSolicitor && (
                  <BulkActionBar
                    count={selectedTasks.size}
                    onClear={() => setSelectedTasks(new Set())}
                    onDelete={handleBulkDeleteTasks}
                    label="task"
                    variant="active"
                    testIdPrefix="tasks"
                  />
                )}
                <div className="space-y-2">
                  {tasks.map((task) => {
                    const isOverdue = task.due_date && task.status !== "completed" && new Date(task.due_date) < new Date(new Date().toISOString().split("T")[0]);
                    const isSel = selectedTasks.has(task.id);
                    return (
                    <div key={task.id} className={`flex items-start gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors ${isSel ? "border-[#100f5d] ring-2 ring-[#100f5d]/15 bg-[#100f5d]/5" : isOverdue ? "border-red-200 bg-red-50/30" : "border-slate-200"}`} data-testid={`task-${task.id}`}>
                      {isSolicitor && (
                        <Checkbox checked={isSel} onCheckedChange={() => toggleTaskSel(task.id)} data-testid={`task-cb-${task.id}`} className="mt-0.5 shrink-0" />
                      )}
                      <button onClick={() => handleToggleTask(task)} data-testid={`toggle-task-${task.id}`}
                        className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${task.status === "completed" ? "bg-emerald-600 border-emerald-600" : "border-slate-300 hover:border-blue-600"}`}>
                        {task.status === "completed" && <Check weight="bold" className="w-3 h-3 text-white" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium ${task.status === "completed" ? "text-slate-400 line-through" : "text-slate-900"}`}>{task.title}</p>
                          {isOverdue && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-600" data-testid={`overdue-badge-${task.id}`}>
                              <Warning weight="fill" className="w-2.5 h-2.5" /> Overdue
                            </span>
                          )}
                        </div>
                        {task.description && <p className="text-xs text-slate-500 mt-0.5">{task.description}</p>}
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          {task.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${isOverdue ? "text-red-600 font-medium" : "text-slate-500"}`}>
                              <CalendarBlank weight="bold" className="w-3 h-3" />
                              {new Date(task.due_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                            </span>
                          )}
                          {task.assigned_to_name && (
                            <span className="text-xs text-slate-500 flex items-center gap-1" data-testid={`task-assignee-${task.id}`}>
                              <UserCircle weight="fill" className="w-3 h-3" /> {task.assigned_to_name}
                            </span>
                          )}
                          {task.created_by_name && <span className="text-xs text-slate-400">by {task.created_by_name}</span>}
                        </div>
                      </div>
                      {isSolicitor && (
                        <button onClick={() => handleDeleteTask(task)} data-testid={`delete-task-${task.id}`}
                          title="Delete task"
                          className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors">
                          <Trash className="w-3.5 h-3.5 text-slate-400 hover:text-red-600" />
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Notes Tab — split into Client and Referrer chat panels */}
          <TabsContent value="notes">
            {(() => {
              const showReferrerPanel = isSolicitor && Boolean(caseData.referrer_id);
              const selectedArr = Array.from(exportSelection).map((k) => {
                const [source, id] = k.split(":");
                return { source, id };
              });

              const isUnseenByMe = (note) => {
                // Author's own messages don't count
                if (note.author_name === user?.name && note.author_role === user?.role) return false;
                const seen = note.seen_by || [];
                return !seen.some((s) => s.actor_name === user?.name && s.actor_role === user?.role);
              };
              const clientUnseen = notes.filter(isUnseenByMe).length;
              const referrerUnseen = referrerNotes.filter(isUnseenByMe).length;

              // Local search filter — matches body / author_name / category /
              // attachment filename. Case-insensitive, substring. Empty query
              // returns the full list unchanged.
              const applySearch = (list, q) => {
                const term = (q || "").trim().toLowerCase();
                if (!term) return list;
                return list.filter((n) => {
                  const hay = [
                    n.content, n.author_name, n.category,
                    ...(n.attachments || []).map((a) => a.original_filename || a.filename || ""),
                  ].filter(Boolean).join(" ").toLowerCase();
                  return hay.includes(term);
                });
              };

              const isSnoozed = (note) => {
                const until = note.snoozed_until;
                return until && new Date(until) > new Date();
              };
              const clientSnoozed = notes.filter(isSnoozed).length;
              const referrerSnoozed = referrerNotes.filter(isSnoozed).length;

              const handleUnsnooze = (source, id) => {
                API.post("/messages/unsnooze", { source, id })
                  .then(() => {
                    if (source === "case_note") {
                      setNotes((prev) => prev.map((n) => n.id === id ? { ...n, snoozed_until: null } : n));
                    } else {
                      setReferrerNotes((prev) => prev.map((n) => n.id === id ? { ...n, snoozed_until: null } : n));
                    }
                    toast.success("Reminder un-snoozed");
                  })
                  .catch(() => toast.error("Failed to un-snooze"));
              };

              const markChannelSeen = (channel) => {
                API.post(`/cases/${caseId}/notes-seen?channel=${channel}`)
                  .then(() => {
                    if (channel === "client") {
                      setNotes((prev) => prev.map((n) => isUnseenByMe(n) ? {
                        ...n,
                        seen_by: [...(n.seen_by || []), { actor_name: user?.name, actor_role: user?.role, seen_at: new Date().toISOString() }],
                      } : n));
                    } else {
                      setReferrerNotes((prev) => prev.map((n) => isUnseenByMe(n) ? {
                        ...n,
                        seen_by: [...(n.seen_by || []), { actor_name: user?.name, actor_role: user?.role, seen_at: new Date().toISOString() }],
                      } : n));
                    }
                  })
                  .catch(() => {});
              };

              const toggleSelect = (source, id) => {
                const key = `${source}:${id}`;
                const next = new Set(exportSelection);
                if (next.has(key)) next.delete(key); else next.add(key);
                setExportSelection(next);
              };

              const renderMessage = (note, source, accent) => {
                const isOwn = note.author_id === user?.id || (source === "referrer_note" && note.author_role === "solicitor" && isSolicitor);
                const key = `${source}:${note.id}`;
                const isSelected = exportSelection.has(key);
                const snoozed = isSnoozed(note);
                // Highlight the specific note the solicitor was pinged
                // about (deep-linked from the Inbox → Messages tab).
                // Match on channel + id so a client and referrer note
                // with the same id (shouldn't happen but be safe) don't
                // both light up.
                const isHighlighted = Boolean(
                  highlightMessageId
                  && note.id === highlightMessageId
                  && ((highlightChannel === "referrer" && source === "referrer_note")
                    || (highlightChannel === "client" && source === "case_note")
                    || !highlightChannel)
                );
                const bubbleOwn = source === "case_note"
                  ? "bg-slate-900 text-white"
                  : "bg-violet-700 text-white";
                const bubbleOther = source === "case_note"
                  ? "bg-blue-50 text-slate-900 border border-blue-100"
                  : "bg-violet-50 text-slate-900 border border-violet-100";
                return (
                  <div
                    key={note.id}
                    id={`msg-${source}-${note.id}`}
                    className={`flex gap-2 ${isOwn ? "flex-row-reverse" : ""} ${isHighlighted ? "scroll-mt-24" : ""}`}
                    data-testid={`${source}-${note.id}`}
                  >
                    <div className="flex flex-col items-center gap-1 shrink-0 pt-1">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${accent.avatarBg}`}>
                        <User weight="bold" className={`w-3.5 h-3.5 ${accent.avatarText}`} />
                      </div>
                      {isSolicitor && (
                        <Checkbox checked={isSelected}
                          onCheckedChange={() => toggleSelect(source, note.id)}
                          className="h-3.5 w-3.5"
                          data-testid={`select-${source}-${note.id}`} />
                      )}
                    </div>
                    <div className={`max-w-[80%] ${isOwn ? "text-right" : ""}`}>
                      <div className={`inline-block px-3 py-2 rounded-lg text-sm ${isOwn ? bubbleOwn : bubbleOther} ${isSelected ? "ring-2 ring-amber-400" : ""} ${snoozed ? "ring-2 ring-amber-300" : ""} ${isHighlighted ? "ring-2 ring-blue-500 animate-pulse" : ""}`} style={{ fontFamily: "Manrope, sans-serif" }}>
                        {note.content}
                      </div>
                      {(note.attachments || []).length > 0 && (
                        <div className={`flex flex-col gap-1.5 mt-1.5 ${isOwn ? "items-end" : "items-start"}`} data-testid={`${source}-attachments-${note.id}`}>
                          {note.attachments.map((a) => (
                            <MessageAttachmentView
                              key={a.id}
                              attachment={a}
                              loadBlob={loadMessageBlob}
                              onDelete={(attId) => deleteSentAttachment(source === "referrer_note" ? "referrer" : "client", note.id, attId)}
                              canDelete={isSolicitor || a.uploaded_by === user?.id}
                              testidPrefix={`${source}-attachment`}
                            />
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 flex-wrap">
                        <span className="font-medium">{note.author_name}</span>
                        <span className={`text-[10px] uppercase tracking-wide font-semibold ${accent.roleText}`}>{note.author_role}</span>
                        <span>·</span>
                        <span>{new Date(note.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                        {snoozed && isSolicitor && (
                          <button
                            data-testid={`unsnooze-${source}-${note.id}`}
                            onClick={(e) => { e.stopPropagation(); handleUnsnooze(source, note.id); }}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold hover:bg-amber-200 transition-colors"
                            title={`Reminder snoozed until ${new Date(note.snoozed_until).toLocaleString("en-GB")}. Click to un-snooze.`}>
                            <Clock weight="bold" className="w-2.5 h-2.5" />Snoozed · click to clear
                          </button>
                        )}
                        {isOwn && source === "case_note" && (
                          <ReadReceipt seenBy={note.seen_by} currentActorName={user?.name} currentActorRole={user?.role} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              };

              const clientAccent = {
                avatarBg: "bg-blue-100",
                avatarText: "text-blue-600",
                roleText: "text-blue-600",
              };
              const referrerAccent = {
                avatarBg: "bg-violet-100",
                avatarText: "text-violet-600",
                roleText: "text-violet-600",
              };

              const ClientPanel = (
                <div className="relative bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden flex flex-col" data-testid="client-chat-panel"
                  onClick={() => { if (clientUnseen > 0) markChannelSeen("client"); }}
                  {...makeChatDrag("client")}>
                  {dragChannel === "client" && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-blue-50/90 border-2 border-dashed border-blue-400 rounded-lg pointer-events-none" data-testid="client-chat-drop-overlay">
                      <div className="flex items-center gap-2 text-sm font-semibold text-blue-700">
                        <UploadSimple weight="bold" className="w-5 h-5" /> Drop to attach
                      </div>
                    </div>
                  )}
                  <div className="px-5 py-3 border-b border-slate-100 bg-blue-50/40 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <ChatCircle weight="fill" className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Outfit, sans-serif" }}>
                          Client conversation
                          {clientUnseen > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="client-unseen-badge">
                              {clientUnseen}
                            </span>
                          )}
                          {clientSnoozed > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 h-[18px] rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="client-snoozed-badge" title="Snoozed reminders on this channel">
                              <Clock weight="bold" className="w-2.5 h-2.5" />{clientSnoozed} snoozed
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500">Visible to clients · {notes.length} message{notes.length === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                    <ChatSearchInput
                      value={clientChatSearch}
                      onChange={setClientChatSearch}
                      testid="client-chat-search"
                    />
                  </div>
                  <div className="p-4 flex-1">
                    {(() => {
                      const filtered = applySearch(notes, clientChatSearch);
                      if (notes.length === 0) {
                        return (
                          <div className="text-center py-10" data-testid="no-client-notes">
                            <ChatCircle weight="thin" className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-xs text-slate-500">No client messages yet.</p>
                          </div>
                        );
                      }
                      if (filtered.length === 0) {
                        return (
                          <div className="text-center py-10" data-testid="no-client-search-matches">
                            <p className="text-xs text-slate-500">No messages match &ldquo;{clientChatSearch}&rdquo;.</p>
                            <button onClick={() => setClientChatSearch("")} className="text-xs text-blue-600 hover:text-blue-700 mt-1" data-testid="clear-client-chat-search">Clear search</button>
                          </div>
                        );
                      }
                      return (
                        <MessageGalleryProvider images={collectImages([...filtered].reverse())} loadBlob={loadMessageBlob}>
                          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="client-messages-list">
                            {[...filtered].reverse().map((n) => renderMessage(n, "case_note", clientAccent))}
                            <div ref={clientMessagesEndRef} />
                          </div>
                        </MessageGalleryProvider>
                      );
                    })()}
                  </div>
                  <form onSubmit={(e) => { sendTyping(false); handleSendNote(e); }} className="border-t border-slate-200 p-3 space-y-2" data-testid="note-form">
                    {(peers.length > 0 || typingActors.length > 0) && (
                      <PresenceBar peers={peers} typingActors={typingActors} />
                    )}
                    <p className="text-[11px] text-slate-500 leading-snug" data-testid="client-reply-disclaimer">
                      <Clock weight="bold" className="w-3 h-3 inline-block mr-1 -mt-0.5 text-slate-400" />
                      Whilst this is an instant message, replies follow standard response times. We aim to review messages within <span className="font-medium text-slate-600">48 hours</span> — at busy times this may be longer.
                    </p>
                    {noteFiles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5" data-testid="note-pending-attachments">
                        {noteFiles.map((a) => (
                          <span key={a.id} className="inline-flex items-center gap-1 text-[11px] bg-blue-50 border border-blue-200 rounded-full pl-1 pr-1 py-0.5 text-blue-800" data-testid={`note-pending-${a.id}`}>
                            {a.preview
                              ? <img src={a.preview} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" data-testid={`note-pending-thumb-${a.id}`} />
                              : <Paperclip weight="bold" className="w-2.5 h-2.5 ml-1" />}
                            <span className="max-w-[140px] truncate">{a.original_filename}</span>
                            <button type="button" onClick={() => removePendingAttachment("client", a.id)} className="hover:text-rose-600" data-testid={`note-pending-remove-${a.id}`}>
                              <X weight="bold" className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input ref={noteFileInputRef} type="file" multiple className="hidden" data-testid="note-file-input"
                        onChange={(e) => { uploadMessageAttachment("client", e.target.files); e.target.value = ""; }} />
                      <Button type="button" variant="outline" size="icon" disabled={uploadingNoteFile}
                        onClick={() => noteFileInputRef.current?.click()}
                        className="h-10 w-10 shrink-0 border-slate-200 text-slate-500 hover:text-blue-600"
                        data-testid="note-attach-btn" title="Upload a file from your computer">
                        {uploadingNoteFile
                          ? <span className="w-4 h-4 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
                          : <Paperclip weight="bold" className="w-4 h-4" />}
                      </Button>
                      <Button type="button" variant="outline" size="icon"
                        onClick={() => { setDocPickerSearch(""); setDocPickerChannel("client"); }}
                        className="h-10 w-10 shrink-0 border-slate-200 text-slate-500 hover:text-blue-600"
                        data-testid="note-attach-doc-btn" title="Attach a file already on this case">
                        <Files weight="bold" className="w-4 h-4" />
                      </Button>
                      <Input data-testid="note-input" placeholder="Message the client…" value={noteText}
                        onChange={(e) => {
                          setNoteText(e.target.value);
                          if (e.target.value.trim()) sendTyping(true); else sendTyping(false);
                        }}
                        onBlur={() => sendTyping(false)}
                        onPaste={handleChatPaste("client")}
                        className="flex-1" />
                      <Button type="submit" data-testid="send-note-btn" disabled={sendingNote || (!noteText.trim() && noteFiles.length === 0)} className="bg-blue-600 hover:bg-blue-700 text-white h-10 w-10 p-0">
                        <PaperPlaneTilt weight="fill" className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </div>
              );

              const ReferrerPanel = showReferrerPanel ? (
                <div className="relative bg-white border border-violet-200 rounded-lg shadow-sm overflow-hidden flex flex-col" data-testid="referrer-chat-panel"
                  onClick={() => { if (referrerUnseen > 0) markChannelSeen("referrer"); }}
                  {...makeChatDrag("referrer")}>
                  {dragChannel === "referrer" && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-violet-50/90 border-2 border-dashed border-violet-400 rounded-lg pointer-events-none" data-testid="referrer-chat-drop-overlay">
                      <div className="flex items-center gap-2 text-sm font-semibold text-violet-700">
                        <UploadSimple weight="bold" className="w-5 h-5" /> Drop to attach
                      </div>
                    </div>
                  )}
                  <div className="px-5 py-3 border-b border-violet-100 bg-violet-50/60 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                        <UserCircle weight="fill" className="w-4 h-4 text-violet-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap" style={{ fontFamily: "Outfit, sans-serif" }}>
                          Referrer conversation
                          {caseData.referrer_name && <span className="text-slate-500 font-normal">· {caseData.referrer_name}</span>}
                          {referrerUnseen > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="referrer-unseen-badge">
                              {referrerUnseen}
                            </span>
                          )}
                          {referrerSnoozed > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 h-[18px] rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="referrer-snoozed-badge" title="Snoozed reminders on this channel">
                              <Clock weight="bold" className="w-2.5 h-2.5" />{referrerSnoozed} snoozed
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-violet-700">Private — not visible to clients · {referrerNotes.length} message{referrerNotes.length === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                    <ChatSearchInput
                      value={referrerChatSearch}
                      onChange={setReferrerChatSearch}
                      testid="referrer-chat-search"
                      tone="violet"
                    />
                  </div>
                  <div className="p-4 flex-1">
                    {(() => {
                      const filtered = applySearch(referrerNotes, referrerChatSearch);
                      if (referrerNotes.length === 0) {
                        return (
                          <div className="text-center py-10" data-testid="no-referrer-notes">
                            <ChatCircle weight="thin" className="w-10 h-10 text-violet-200 mx-auto mb-2" />
                            <p className="text-xs text-slate-500">No referrer messages yet.</p>
                          </div>
                        );
                      }
                      if (filtered.length === 0) {
                        return (
                          <div className="text-center py-10" data-testid="no-referrer-search-matches">
                            <p className="text-xs text-slate-500">No messages match &ldquo;{referrerChatSearch}&rdquo;.</p>
                            <button onClick={() => setReferrerChatSearch("")} className="text-xs text-violet-600 hover:text-violet-700 mt-1" data-testid="clear-referrer-chat-search">Clear search</button>
                          </div>
                        );
                      }
                      return (
                        <MessageGalleryProvider images={collectImages([...filtered].reverse())} loadBlob={loadMessageBlob}>
                          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="referrer-messages-list">
                            {[...filtered].reverse().map((n) => renderMessage(n, "referrer_note", referrerAccent))}
                            <div ref={referrerMessagesEndRef} />
                          </div>
                        </MessageGalleryProvider>
                      );
                    })()}
                  </div>
                  <form onSubmit={handleSendReferrerNote} className="border-t border-violet-100 p-3 space-y-2" data-testid="referrer-note-form">
                    <p className="text-[11px] text-slate-500 leading-snug" data-testid="referrer-reply-disclaimer">
                      <Clock weight="bold" className="w-3 h-3 inline-block mr-1 -mt-0.5 text-slate-400" />
                      Whilst this is an instant message, replies follow standard response times. We aim to review messages within <span className="font-medium text-slate-600">48 hours</span> — at busy times this may be longer.
                    </p>
                    {referrerNoteFiles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5" data-testid="referrer-note-pending-attachments">
                        {referrerNoteFiles.map((a) => (
                          <span key={a.id} className="inline-flex items-center gap-1 text-[11px] bg-violet-50 border border-violet-200 rounded-full pl-1 pr-1 py-0.5 text-violet-800" data-testid={`referrer-note-pending-${a.id}`}>
                            {a.preview
                              ? <img src={a.preview} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" data-testid={`referrer-note-pending-thumb-${a.id}`} />
                              : <Paperclip weight="bold" className="w-2.5 h-2.5 ml-1" />}
                            <span className="max-w-[140px] truncate">{a.original_filename}</span>
                            <button type="button" onClick={() => removePendingAttachment("referrer", a.id)} className="hover:text-rose-600" data-testid={`referrer-note-pending-remove-${a.id}`}>
                              <X weight="bold" className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input ref={referrerFileInputRef} type="file" multiple className="hidden" data-testid="referrer-note-file-input"
                        onChange={(e) => { uploadMessageAttachment("referrer", e.target.files); e.target.value = ""; }} />
                      <Button type="button" variant="outline" size="icon" disabled={uploadingReferrerFile}
                        onClick={() => referrerFileInputRef.current?.click()}
                        className="h-10 w-10 shrink-0 border-violet-200 text-slate-500 hover:text-violet-600"
                        data-testid="referrer-note-attach-btn" title="Upload a file from your computer">
                        {uploadingReferrerFile
                          ? <span className="w-4 h-4 border-2 border-slate-300 border-t-violet-600 rounded-full animate-spin" />
                          : <Paperclip weight="bold" className="w-4 h-4" />}
                      </Button>
                      <Button type="button" variant="outline" size="icon"
                        onClick={() => { setDocPickerSearch(""); setDocPickerChannel("referrer"); }}
                        className="h-10 w-10 shrink-0 border-violet-200 text-slate-500 hover:text-violet-600"
                        data-testid="referrer-note-attach-doc-btn" title="Attach a file already on this case">
                        <Files weight="bold" className="w-4 h-4" />
                      </Button>
                      <Input data-testid="referrer-note-input" placeholder="Message the referrer…" value={referrerNoteText}
                        onChange={(e) => setReferrerNoteText(e.target.value)}
                        onPaste={handleChatPaste("referrer")}
                        className="flex-1" />
                      <Button type="submit" data-testid="send-referrer-note-btn" disabled={sendingReferrerNote || (!referrerNoteText.trim() && referrerNoteFiles.length === 0)} className="bg-violet-600 hover:bg-violet-700 text-white h-10 w-10 p-0">
                        <PaperPlaneTilt weight="fill" className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </div>
              ) : null;

              return (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Communication</h3>
                    {isSolicitor && (
                      <div className="flex items-center gap-2">
                        {exportSelection.size > 0 && (
                          <>
                            <span className="text-xs text-slate-500" data-testid="selection-count">{exportSelection.size} selected</span>
                            <Button variant="outline" size="sm" onClick={() => setExportSelection(new Set())} className="h-8 text-xs">Clear</Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setExportOpen(true)} className="h-8 text-xs" data-testid="open-export-btn">
                          <DownloadSimple weight="bold" className="w-3.5 h-3.5 mr-1" />
                          Export
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className={`grid gap-4 ${showReferrerPanel ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
                    {ClientPanel}
                    {ReferrerPanel}
                  </div>
                  {isSolicitor && (
                    <MessageExportDialog
                      open={exportOpen}
                      onOpenChange={setExportOpen}
                      caseId={caseId}
                      selectedIds={selectedArr}
                    />
                  )}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="communications">
            {isSolicitor && <CommunicationsTab
              caseId={caseId}
              isSolicitor={isSolicitor}
              caseClients={caseData?.clients || []}
              caseParties={caseData?.parties || []}
              caseReferrer={caseData?.referrer || null}
              legacyClientEmail={caseData?.client_email || ""}
              propertyAddress={caseData?.property_address || ""}
            />}
          </TabsContent>

          <TabsContent value="accounts">
            {isSolicitor && <CaseAccountsTab caseId={caseId} />}
          </TabsContent>

          <TabsContent value="authorities">
            {isSolicitor && <AuthoritiesTab caseId={caseId} caseData={caseData} isSolicitor={isSolicitor} />}
          </TabsContent>

          <TabsContent value="onboarding">
            {isSolicitor && <OnboardingStatusPanel caseId={caseId} />}
          </TabsContent>

          <TabsContent value="forms">
            <QuestionnaireCard caseId={caseId} viewerRole={isSolicitor ? "solicitor" : "client"} />
          </TabsContent>
        </Tabs>
      </main>

      <DocumentViewerDialog
        doc={viewerDoc}
        documents={documents}
        folders={folders}
        open={!!viewerDoc}
        onClose={closeViewer}
        initialPage={viewerInitialPage}
        highlightTerms={viewerHighlight}
      />

      {/* Native e-signing: pick mode/title for a chosen PDF, then design fields. */}
      {esignModeDoc && (
        <SendForSigningDialog
          caseId={caseId}
          doc={esignModeDoc}
          documents={documents}
          onClose={() => setEsignModeDoc(null)}
          onCreated={(env) => { setEsignModeDoc(null); setEsignDesigner(env); }}
        />
      )}
      {esignDesigner && (
        <Dialog open onOpenChange={(o) => { if (!o) { setEsignDesigner(null); setEsignRefresh((x) => x + 1); } }}>
          <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] p-0 gap-0 flex flex-col" data-testid="designer-dialog">
            <DialogHeader className="sr-only">
              <DialogTitle>Signature field designer — {esignDesigner.title}</DialogTitle>
              <DialogDescription>Assign parties and place signature, name, address and date fields on the document.</DialogDescription>
            </DialogHeader>
            <FieldDesigner
              envelope={esignDesigner}
              onClose={() => { setEsignDesigner(null); setEsignRefresh((x) => x + 1); }}
              onSent={() => { setEsignDesigner(null); setEsignRefresh((x) => x + 1); }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* DocuSign "Send for signature" dialog — two clearly separated
          modes:
            • Standard e-signature: contracts, care letters, internal
              reports, ID forms. Simple one-signer flow with optional
              witness.
            • HMLR-compliant deed: TR1/TP1/DS1/mortgages. Enforces SMS
              access-code auth on the client, in-person witness
              routing, and auto-generates the PG82 conveyancer's
              certificate on completion. */}
      <Dialog
        open={sigDialog.open}
        onOpenChange={(open) => !open && resetSigDialog()}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="send-for-sig-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Send for signature</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Choose the signing mode. Standard for contracts, care letters and internal documents. HMLR-compliant for registrable deeds (TR1, TP1, DS1, legal charges).
            </DialogDescription>
          </DialogHeader>
          {sigDialog.doc && (
            <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-md text-xs" data-testid="send-for-sig-doc-name">
              <span className="font-medium text-slate-900">{sigDialog.doc.original_filename || sigDialog.doc.filename}</span>
            </div>
          )}

          {/* Mode selector — full-width segmented control */}
          <div className="grid grid-cols-2 gap-2" data-testid="sig-mode-selector">
            <button
              type="button"
              data-testid="sig-mode-standard"
              onClick={() => setSigDialog((s) => ({ ...s, mode: "standard" }))}
              className={`text-left p-3 rounded-md border transition-all ${
                sigDialog.mode === "standard"
                  ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className="text-xs font-semibold text-slate-900">Standard e-signature</div>
              <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">Contracts, care letters, ID forms — one-tap send.</div>
            </button>
            <button
              type="button"
              data-testid="sig-mode-tr1"
              onClick={() => setSigDialog((s) => ({ ...s, mode: "tr1", include_witness: true }))}
              className={`text-left p-3 rounded-md border transition-all ${
                sigDialog.mode === "tr1"
                  ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className="text-xs font-semibold text-slate-900 flex items-center gap-1">
                HMLR-compliant deed
                <span className="text-[9px] bg-emerald-600 text-white px-1 py-0.5 rounded">PG82</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">TR1 / TP1 / DS1 / charges — SMS auth, in-person witness, auto PG82.</div>
            </button>
          </div>

          {sigDialog.mode === "tr1" && (
            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-md text-[11px] text-emerald-900 leading-relaxed">
              <span className="font-semibold">Practice Guide 8 §13 enforced:</span> The client authenticates via SMS access code, then the witness signs on the same device in the same session. A pre-filled PG82 certificate is auto-filed to this case on completion.
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label htmlFor="sig-signer-name" className="text-xs">Signer name</Label>
              <Input
                id="sig-signer-name" data-testid="sig-signer-name"
                value={sigDialog.signer_name}
                onChange={(e) => setSigDialog((s) => ({ ...s, signer_name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            <div>
              <Label htmlFor="sig-signer-email" className="text-xs">Signer email</Label>
              <Input
                id="sig-signer-email" data-testid="sig-signer-email" type="email"
                value={sigDialog.signer_email}
                onChange={(e) => setSigDialog((s) => ({ ...s, signer_email: e.target.value }))}
                placeholder="name@example.com"
              />
            </div>
            {sigDialog.mode === "tr1" && (
              <div>
                <Label htmlFor="sig-signer-phone" className="text-xs">
                  Signer UK mobile <span className="text-rose-600">*</span>
                </Label>
                <Input
                  id="sig-signer-phone" data-testid="sig-signer-phone" type="tel"
                  value={sigDialog.signer_phone}
                  onChange={(e) => setSigDialog((s) => ({ ...s, signer_phone: e.target.value }))}
                  placeholder="07700 900123"
                />
                <p className="text-[10px] text-slate-500 mt-1">SMS access code is sent to this number before the deed can be opened.</p>
              </div>
            )}
            <div>
              <Label htmlFor="sig-subject" className="text-xs">Email subject</Label>
              <Input
                id="sig-subject" data-testid="sig-subject"
                value={sigDialog.email_subject}
                onChange={(e) => setSigDialog((s) => ({ ...s, email_subject: e.target.value }))}
                maxLength={100}
              />
            </div>
            <div>
              <Label htmlFor="sig-blurb" className="text-xs">Message</Label>
              <Textarea
                id="sig-blurb" data-testid="sig-blurb"
                value={sigDialog.email_blurb}
                onChange={(e) => setSigDialog((s) => ({ ...s, email_blurb: e.target.value }))}
                rows={3} maxLength={500}
              />
            </div>

            {/* Witness section — always visible in TR1 mode (forced on),
                collapsible in Standard mode (optional). */}
            {sigDialog.mode === "standard" && (
              <div className="border-t border-slate-200 pt-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sig-include-witness"
                    data-testid="sig-include-witness"
                    checked={sigDialog.include_witness}
                    onCheckedChange={(v) => setSigDialog((s) => ({ ...s, include_witness: !!v }))}
                  />
                  <Label htmlFor="sig-include-witness" className="text-xs cursor-pointer">
                    Include a witness (non-deed only)
                  </Label>
                </div>
                <p className="text-[10px] text-slate-500 mt-1 pl-6">
                  For registrable deeds (TR1, TP1, DS1), switch to <span className="font-medium">HMLR-compliant deed</span> mode above instead.
                </p>
              </div>
            )}

            {(sigDialog.mode === "tr1" || sigDialog.include_witness) && (
              <div className={`space-y-3 pl-1 ${sigDialog.mode === "tr1" ? "border-t border-emerald-200 pt-3" : ""}`}>
                {sigDialog.mode === "tr1" && (
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    The witness must be physically present with the client. DocuSign will render the witness screen on the same device immediately after the client signs.
                  </p>
                )}
                <div>
                  <Label htmlFor="sig-witness-name" className="text-xs">
                    Witness name {sigDialog.mode === "tr1" && <span className="text-rose-600">*</span>}
                  </Label>
                  <Input
                    id="sig-witness-name" data-testid="sig-witness-name"
                    value={sigDialog.witness_name}
                    onChange={(e) => setSigDialog((s) => ({ ...s, witness_name: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label htmlFor="sig-witness-email" className="text-xs">
                    Witness email {sigDialog.mode === "tr1" && <span className="text-rose-600">*</span>}
                  </Label>
                  <Input
                    id="sig-witness-email" data-testid="sig-witness-email" type="email"
                    value={sigDialog.witness_email}
                    onChange={(e) => setSigDialog((s) => ({ ...s, witness_email: e.target.value }))}
                    placeholder="witness@example.com"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={resetSigDialog}
              data-testid="send-for-sig-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendForSignature}
              disabled={sigDialog.sending}
              data-testid="send-for-sig-confirm"
              className={sigDialog.mode === "tr1"
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"}
            >
              {sigDialog.sending
                ? "Sending…"
                : sigDialog.mode === "tr1"
                  ? "Send HMLR-compliant deed"
                  : "Send for signature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test deed dry-run dialog — sends a pre-populated TR1 to the
          calling solicitor for end-to-end validation of the HMLR
          compliance stack (SMS access code, in-person witness, PG82). */}
      <Dialog
        open={testDeedDialog.open}
        onOpenChange={(open) => !open && resetTestDeedDialog()}
      >
        <DialogContent className="sm:max-w-md" data-testid="test-deed-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Test TR1 dry-run</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Sends a TR1 test deed to your own email. Validates SMS auth, in-person witness routing, and auto-PG82. No legal effect — purely for compliance testing.
            </DialogDescription>
          </DialogHeader>
          <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-md text-[11px] text-emerald-900 leading-relaxed">
            You will be both the solicitor (sender) and the client (signer). Use your own UK mobile so the SMS code lands on your phone. Nominate any colleague or personal account as the witness.
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="test-deed-phone" className="text-xs">Your UK mobile</Label>
              <Input
                id="test-deed-phone" data-testid="test-deed-phone" type="tel"
                value={testDeedDialog.signer_phone}
                onChange={(e) => setTestDeedDialog((s) => ({ ...s, signer_phone: e.target.value }))}
                placeholder="07700 900123"
              />
              <p className="text-[10px] text-slate-500 mt-1">DocuSign will text the access code to this number before the envelope can be opened.</p>
            </div>
            <div>
              <Label htmlFor="test-deed-witness-name" className="text-xs">Witness name</Label>
              <Input
                id="test-deed-witness-name" data-testid="test-deed-witness-name"
                value={testDeedDialog.witness_name}
                onChange={(e) => setTestDeedDialog((s) => ({ ...s, witness_name: e.target.value }))}
                placeholder="Colleague full name"
              />
            </div>
            <div>
              <Label htmlFor="test-deed-witness-email" className="text-xs">Witness email</Label>
              <Input
                id="test-deed-witness-email" data-testid="test-deed-witness-email" type="email"
                value={testDeedDialog.witness_email}
                onChange={(e) => setTestDeedDialog((s) => ({ ...s, witness_email: e.target.value }))}
                placeholder="colleague@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetTestDeedDialog} data-testid="test-deed-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSendTestDeed}
              disabled={testDeedDialog.sending}
              data-testid="test-deed-confirm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {testDeedDialog.sending ? "Sending…" : "Send test deed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={promoteLeadOpen} onOpenChange={setPromoteLeadOpen}>
        <DialogContent className="max-w-md" data-testid="case-promote-lead-dialog">
          <DialogHeader>
            <DialogTitle>Promote {leadInfo?.name} to Client Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Set an initial password and the lead becomes a real client account ({leadInfo?.email}). They'll be attached to this case automatically. Share the password with them so they can log in.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Initial password (min 6 chars)</Label>
              <Input data-testid="case-promote-lead-pwd" type="text" value={promoteLeadPwd} onChange={(e) => setPromoteLeadPwd(e.target.value)} placeholder="TempPass123!" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteLeadOpen(false)} disabled={promotingLead}>Cancel</Button>
            <Button onClick={handlePromoteLead} disabled={promotingLead}
              data-testid="case-promote-lead-confirm"
              className="text-white hover:opacity-90 gap-1.5 bg-amber-600 hover:bg-amber-700">
              <Sparkle weight="bold" className="w-3.5 h-3.5" />
              {promotingLead ? "Promoting…" : "Promote to client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client details dialog — opens when a row's pencil/View button is
          clicked. Solicitors get full edit; everyone else gets a read-only
          view via the `readOnly` prop. */}
      <ClientDetailsDialog
        open={!!editingClient}
        onOpenChange={(open) => !open && setEditingClient(null)}
        caseId={caseId}
        client={editingClient}
        readOnly={!isSolicitor}
        onSaved={(updated) => setCaseData(updated)}
      />

      {/* Edit case header — solicitor-only quick editor for the case name and
          property address (corrects typos / new property info post-instruction). */}
      <EditCaseHeaderDialog
        open={editCaseHeaderOpen}
        onOpenChange={setEditCaseHeaderOpen}
        caseData={caseData}
        onSaved={(updated) => {
          setCaseData((prev) => ({ ...prev, ...updated }));
        }}
      />
      {/* PII inheritance Review-and-Accept panel — surfaces the diff
          before any prior-case data lands on the file. */}
      {inheritanceReview && (
        <ReviewInheritanceDialog
          open={!!inheritanceReview}
          onOpenChange={(v) => { if (!v) setInheritanceReview(null); }}
          caseId={caseId}
          clientKey={inheritanceReview.key.startsWith("pending:")
            ? inheritanceReview.key.slice("pending:".length)
            : inheritanceReview.key}
          clientEntry={inheritanceReview.entry}
          onReviewed={(updated) => { setCaseData(updated); setInheritanceReview(null); }}
        />
      )}

      {/* Attach a file already on this case to a conversation message (global so it works on any tab) */}
      <Dialog open={!!docPickerChannel} onOpenChange={(o) => { if (!o) setDocPickerChannel(null); }}>
        <DialogContent className="max-w-lg" data-testid="doc-picker-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Attach from case documents</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Search documents…"
            value={docPickerSearch}
            onChange={(e) => setDocPickerSearch(e.target.value)}
            className="mb-2"
            data-testid="doc-picker-search"
          />
          <div className="max-h-[380px] overflow-y-auto -mx-1 px-1 space-y-1">
            {(() => {
              const q = docPickerSearch.trim().toLowerCase();
              const list = (documents || []).filter((d) => !q || (d.original_filename || "").toLowerCase().includes(q));
              if (list.length === 0) {
                return <p className="text-sm text-slate-400 py-8 text-center" data-testid="doc-picker-empty">No documents on this case yet.</p>;
              }
              return list.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => attachFromDocument(docPickerChannel, d)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left transition-colors"
                  data-testid={`doc-picker-item-${d.id}`}
                >
                  <FileText weight="bold" className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-sm text-slate-700">{d.original_filename}</span>
                  <span className="text-xs text-blue-600 shrink-0">Attach</span>
                </button>
              ));
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocPickerChannel(null)} data-testid="doc-picker-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk move/copy target-folder picker */}
      <Dialog open={!!bulkFolderAction} onOpenChange={(o) => { if (!o) setBulkFolderAction(null); }}>
        <DialogContent className="max-w-md" data-testid="bulk-folder-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              {bulkFolderAction === "copy" ? "Copy" : "Move"} {selectedDocs.size} document{selectedDocs.size === 1 ? "" : "s"} to…
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[360px] overflow-y-auto -mx-1 px-1 space-y-1">
            <button
              type="button"
              onClick={() => runBulkFolderAction(null)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left transition-colors"
              data-testid="bulk-folder-root"
            >
              <FolderSimple weight="bold" className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="flex-1 text-sm text-slate-700">Case root (no folder)</span>
            </button>
            {folders.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => runBulkFolderAction(f.id)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left transition-colors"
                data-testid={`bulk-folder-${f.id}`}
              >
                <FolderSimple weight="fill" className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm text-slate-700">{f.name}</span>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkFolderAction(null)} data-testid="bulk-folder-cancel">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
